import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import Icon3D from '../components/Icon3D';
import { useTenant } from '../contexts/TenantContext';
import { BarChart3, Mail, Flag, RefreshCw, CheckCircle, Clock, Settings, Eye, EyeOff, Save, FileText, ChevronDown, ChevronUp, Brain, Target, AlertTriangle, TrendingUp, TrendingDown, Minus, Printer, UserX, UserCheck, GitMerge, Search, ArrowRight, Shield, Wrench, Type, Zap, BookOpen, Download, Upload, Trash2 } from 'lucide-react';
import axios from 'axios';
import { getAdminSyncStatus, triggerAdminSync, getAdminSyncJobStatus, saveAdminSyncConfig, getAdminSyncConfig, testSyncConnection, triggerLocalReseed, getReseedJobStatus, checkMcqDatabaseFile, getDiagnosticReportUsers, generateDiagnosticReport, getDiagnosticReport, generateAdaptiveProgressReport, getAdaptiveProgressReport, getOverallReport, deactivateUser, reactivateUser, mergeUsers, previewQuestionFixes, applyQuestionFixes, previewFindReplace, applyFindReplace, previewQualityFixes, applyQualityFixes, previewEncodingFixes, applyEncodingFixes, getWordFrequency, startSpellingScan, getSpellingScanStatus, exportQuestions, importQuestionsFile, bulkDeleteQuestions } from '../api/endpoints';
import { parsePlan, getTierName, getBaseTier } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSubscription, setFilterSubscription] = useState('all');
  const [actionLoading, setActionLoading] = useState(false);
  const [subscriptionHistory, setSubscriptionHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [diagnosticSummary, setDiagnosticSummary] = useState(null);
  const [syncLogs, setSyncLogs] = useState([]);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncInProgress, setSyncInProgress] = useState(false);
  const [syncConfigSet, setSyncConfigSet] = useState(false);
  const [showSyncSettings, setShowSyncSettings] = useState(false);
  const [syncSecret, setSyncSecret] = useState('');
  const [githubToken, setGithubToken] = useState('');
  const [showSecretValues, setShowSecretValues] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [reseedLoading, setReseedLoading] = useState(false);
  const [reseedResult, setReseedResult] = useState(null);
  const [fileCheckResult, setFileCheckResult] = useState(null);
  const [fileCheckLoading, setFileCheckLoading] = useState(false);
  // Diagnostic report state
  const [reportUsers, setReportUsers] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(null); // user_id being generated
  const [activeReport, setActiveReport] = useState(null);
  const [showReportDetail, setShowReportDetail] = useState(false);
  const [reportSection, setReportSection] = useState('full'); // 'full', '50pct', 'comparison'
  const [expandedCategories, setExpandedCategories] = useState({});
  // Adaptive progress report state
  const [generatingAdaptive, setGeneratingAdaptive] = useState(null);
  const [activeAdaptiveReport, setActiveAdaptiveReport] = useState(null);
  const [showAdaptiveDetail, setShowAdaptiveDetail] = useState(false);
  // Overall report state
  const [loadingOverall, setLoadingOverall] = useState(null);
  const [activeOverallReport, setActiveOverallReport] = useState(null);
  const [showOverallDetail, setShowOverallDetail] = useState(false);
  // User deactivation state
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [deactivateLoading, setDeactivateLoading] = useState(false);
  // User merge state
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeSearchTerm, setMergeSearchTerm] = useState('');
  const [mergeTargetUser, setMergeTargetUser] = useState(null);
  const [showMergeConfirm, setShowMergeConfirm] = useState(false);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [mergeResult, setMergeResult] = useState(null);
  // Question text fix state
  const [fixPreview, setFixPreview] = useState(null);
  const [fixPreviewLoading, setFixPreviewLoading] = useState(false);
  const [fixApplyLoading, setFixApplyLoading] = useState(false);
  const [fixApplyResult, setFixApplyResult] = useState(null);
  const [showFixConfirm, setShowFixConfirm] = useState(false);
  // Custom Find & Replace state
  const [frFind, setFrFind] = useState('');
  const [frReplace, setFrReplace] = useState('');
  const [frFields, setFrFields] = useState(['question']);
  const [frCaseSensitive, setFrCaseSensitive] = useState(false);
  const [frPreview, setFrPreview] = useState(null);
  const [frPreviewLoading, setFrPreviewLoading] = useState(false);
  const [frApplyLoading, setFrApplyLoading] = useState(false);
  const [frApplyResult, setFrApplyResult] = useState(null);
  const [showFrConfirm, setShowFrConfirm] = useState(false);
  // Text Quality Fixes state
  const [qualityPreview, setQualityPreview] = useState(null);
  const [qualityPreviewLoading, setQualityPreviewLoading] = useState(false);
  const [qualityApplyLoading, setQualityApplyLoading] = useState(false);
  const [qualityApplyResult, setQualityApplyResult] = useState(null);
  const [showQualityConfirm, setShowQualityConfirm] = useState(false);
  // Encoding Fixes state
  const [encodingPreview, setEncodingPreview] = useState(null);
  const [encodingPreviewLoading, setEncodingPreviewLoading] = useState(false);
  const [encodingApplyLoading, setEncodingApplyLoading] = useState(false);
  const [encodingApplyResult, setEncodingApplyResult] = useState(null);
  const [showEncodingConfirm, setShowEncodingConfirm] = useState(false);
  // Word Frequency Scanner state
  const [wordScanResults, setWordScanResults] = useState(null);
  const [wordScanLoading, setWordScanLoading] = useState(false);
  const [wordMinLength, setWordMinLength] = useState(2);
  const [wordMaxLength, setWordMaxLength] = useState(5);
  const [wordMinCount, setWordMinCount] = useState(3);
  const [wordSpellCheck, setWordSpellCheck] = useState(false);
  const [wordMaxCount, setWordMaxCount] = useState(49);
  const [spellingStep, setSpellingStep] = useState('');
  const [wordPage, setWordPage] = useState(0);
  const WORDS_PER_PAGE = 50;
  // Export / Import state
  const [exportLoading, setExportLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const { tenant } = useTenant();

  // Get tenant branding
  const tenantName = tenant?.name || 'MedMCQ';

  useEffect(() => {
    document.title = `Admin | ${tenantName}`;
  }, [tenantName]);

  useEffect(() => {
    fetchUsers();
    fetchStats();
    fetchDiagnosticSummary();
    fetchSyncStatus();
    fetchReportUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      alert('Failed to fetch users. Make sure you have admin access.');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchDiagnosticSummary = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/diagnostic/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDiagnosticSummary(response.data);
    } catch (error) {
      console.error('Error fetching diagnostic summary:', error);
    }
  };

  // ── Diagnostic Report Functions ──

  const fetchReportUsers = async () => {
    try {
      const response = await getDiagnosticReportUsers();
      setReportUsers(response.data);
    } catch (error) {
      console.error('Error fetching report users:', error);
    }
  };

  const handleGenerateReport = async (userId) => {
    setGeneratingReport(userId);
    try {
      const response = await generateDiagnosticReport(userId);
      setActiveReport(response.data);
      setShowReportDetail(true);
      setReportSection('full');
      // Refresh user list to show updated report status
      fetchReportUsers();
    } catch (error) {
      alert('Failed to generate report: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGeneratingReport(null);
    }
  };

  const handleViewReport = async (userId) => {
    try {
      const response = await getDiagnosticReport(userId);
      setActiveReport(response.data);
      setShowReportDetail(true);
      setReportSection('full');
    } catch (error) {
      alert('No report found. Generate one first.');
    }
  };

  const handleGenerateAdaptiveReport = async (userId) => {
    setGeneratingAdaptive(userId);
    try {
      const response = await generateAdaptiveProgressReport(userId);
      setActiveAdaptiveReport(response.data);
      setShowAdaptiveDetail(true);
    } catch (error) {
      alert('Failed to generate adaptive progress report: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGeneratingAdaptive(null);
    }
  };

  const handleViewOverallReport = async (userId) => {
    setLoadingOverall(userId);
    try {
      const response = await getOverallReport(userId);
      setActiveOverallReport(response.data);
      setShowOverallDetail(true);
    } catch (error) {
      alert('Failed to load overall report: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoadingOverall(null);
    }
  };

  const toggleCategory = (cat) => {
    setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  const formatMs = (ms) => {
    if (!ms || ms === 0) return '—';
    if (ms < 1000) return `${Math.round(ms)}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const formatPct = (val) => {
    if (val === null || val === undefined) return '—';
    return `${(val * 100).toFixed(1)}%`;
  };

  const getAccuracyColor = (acc) => {
    if (acc >= 0.85) return 'text-emerald-600';
    if (acc >= 0.70) return 'text-cyan-600';
    if (acc >= 0.50) return 'text-amber-600';
    return 'text-red-600';
  };

  const getAccuracyBg = (acc) => {
    if (acc >= 0.85) return 'bg-emerald-100 text-emerald-700';
    if (acc >= 0.70) return 'bg-cyan-100 text-cyan-700';
    if (acc >= 0.50) return 'bg-amber-100 text-amber-700';
    return 'bg-red-100 text-red-700';
  };

  const fetchSyncStatus = async () => {
    try {
      const response = await getAdminSyncStatus();
      setSyncLogs(response.data.logs || []);
      setSyncInProgress(response.data.sync_in_progress || false);
      setSyncConfigSet(response.data.config_set || false);
    } catch (error) {
      console.error('Error fetching sync status:', error);
    }
  };

  const handleSaveConfig = async () => {
    if (!syncSecret.trim() || !githubToken.trim()) {
      alert('Both fields are required.');
      return;
    }
    setSavingConfig(true);
    try {
      await saveAdminSyncConfig({ sync_secret: syncSecret, github_token: githubToken });
      setSyncConfigSet(true);
      setShowSyncSettings(false);
      setSyncSecret('');
      setGithubToken('');
      alert('Sync configuration saved successfully!');
    } catch (error) {
      alert('Failed to save config: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSavingConfig(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      const response = await testSyncConnection();
      if (response.data.success) {
        alert('✅ ' + response.data.message);
      } else {
        alert('❌ ' + response.data.error);
      }
    } catch (error) {
      alert('Connection test failed: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleTriggerSync = async () => {
    setSyncLoading(true);
    try {
      const response = await triggerAdminSync();
      const jobId = response.data.job_id;
      setSyncInProgress(true);

      // Poll for completion (sync can take 2-3 minutes for large files)
      let pollCount = 0;
      const maxPolls = 120; // 120 x 3s = 6 minutes max
      const pollInterval = setInterval(async () => {
        pollCount++;
        try {
          const statusResp = await getAdminSyncJobStatus(jobId);
          if (statusResp.data.status === 'done') {
            clearInterval(pollInterval);
            setSyncLoading(false);
            setSyncInProgress(false);
            fetchSyncStatus();
            alert(`Sync complete! ${statusResp.data.inserted} inserted, ${statusResp.data.updated} updated, ${statusResp.data.unchanged} unchanged.`);
          } else if (statusResp.data.status === 'error') {
            clearInterval(pollInterval);
            setSyncLoading(false);
            setSyncInProgress(false);
            alert(`Sync failed: ${statusResp.data.error}`);
          } else if (pollCount >= maxPolls) {
            clearInterval(pollInterval);
            setSyncLoading(false);
            setSyncInProgress(false);
            alert('Sync is taking longer than expected. It may still be running in the background. Refresh the page in a few minutes to check.');
          }
        } catch (err) {
          // Don't stop polling on transient network errors — only stop after multiple failures
          if (pollCount >= maxPolls) {
            clearInterval(pollInterval);
            setSyncLoading(false);
            setSyncInProgress(false);
            alert('Lost connection while checking sync status. The sync may still be running. Refresh the page in a few minutes.');
          }
        }
      }, 3000);
    } catch (error) {
      setSyncLoading(false);
      setSyncInProgress(false);
      if (error.response?.status === 409) {
        alert('A sync is already in progress. Please wait.');
      } else {
        alert('Failed to trigger sync: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const handleCheckFile = async () => {
    setFileCheckLoading(true);
    setFileCheckResult(null);
    try {
      const response = await checkMcqDatabaseFile();
      setFileCheckResult(response.data);
    } catch (error) {
      setFileCheckResult({ status: 'error', error: error.response?.data?.detail || error.message });
    } finally {
      setFileCheckLoading(false);
    }
  };

  const handleLocalReseed = async () => {
    setReseedLoading(true);
    setReseedResult(null);
    try {
      const response = await triggerLocalReseed();
      const jobId = response.data.job_id;

      // Poll for completion (reseed can take several minutes for 89k+ questions)
      let pollCount = 0;
      const maxPolls = 180; // 180 x 3s = 9 minutes max
      const pollInterval = setInterval(async () => {
        pollCount++;
        try {
          const statusResp = await getReseedJobStatus(jobId);
          if (statusResp.data.status === 'done') {
            clearInterval(pollInterval);
            setReseedLoading(false);
            setReseedResult(statusResp.data);
            alert(`Re-seed complete! ${statusResp.data.inserted} inserted, ${statusResp.data.updated} updated, ${statusResp.data.unchanged} unchanged, ${statusResp.data.skipped} skipped. Total: ${statusResp.data.imported}`);
          } else if (statusResp.data.status === 'error') {
            clearInterval(pollInterval);
            setReseedLoading(false);
            alert(`Re-seed failed: ${statusResp.data.error}`);
          } else if (pollCount >= maxPolls) {
            clearInterval(pollInterval);
            setReseedLoading(false);
            alert('Re-seed is taking longer than expected. It may still be running in the background.');
          }
        } catch (err) {
          if (pollCount >= maxPolls) {
            clearInterval(pollInterval);
            setReseedLoading(false);
            alert('Lost connection while checking re-seed status. It may still be running.');
          }
        }
      }, 3000);
    } catch (error) {
      setReseedLoading(false);
      alert('Failed to start re-seed: ' + (error.response?.data?.detail || error.message));
    }
  };

  // ── Export all questions as NDJSON file ──
  const handleExportQuestions = async () => {
    setExportLoading(true);
    try {
      const response = await exportQuestions();
      // Create a download link from the blob
      const blob = new Blob([response.data], { type: 'application/x-ndjson' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `medmcq_questions_export_${new Date().toISOString().slice(0,10)}.ndjson`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setExportLoading(false);
    } catch (error) {
      setExportLoading(false);
      alert('Export failed: ' + (error.response?.data?.detail || error.message));
    }
  };

  // ── Import questions from NDJSON file ──
  const handleImportQuestions = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!window.confirm(`Import ${file.name} (${(file.size / 1024 / 1024).toFixed(1)} MB)?\n\nThis will update existing questions and add new ones. No questions will be deleted.\n\nLarge files are automatically split into chunks.`)) {
      event.target.value = '';
      return;
    }

    setImportLoading(true);
    setImportResult(null);
    try {
      const text = await file.text();
      const lines = text.trim().split('\n').filter(l => l.trim());

      // Split into chunks of 5000 questions (~5-6 MB each) to avoid 413 errors
      const CHUNK_SIZE = 5000;
      const totalChunks = Math.ceil(lines.length / CHUNK_SIZE);
      let totalUpserted = 0;
      let totalSkipped = 0;
      let totalErrors = 0;

      for (let i = 0; i < totalChunks; i++) {
        const chunkLines = lines.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        const chunkData = chunkLines.join('\n');

        // Update button text with progress
        setImportResult({ progress: `Chunk ${i + 1}/${totalChunks} (${((i + 1) / totalChunks * 100).toFixed(0)}%)` });

        const response = await importQuestionsFile(chunkData);
        totalUpserted += response.data.upserted || 0;
        totalSkipped += response.data.skipped || 0;
        totalErrors += response.data.errors || 0;
      }

      const result = { upserted: totalUpserted, skipped: totalSkipped, errors: totalErrors };
      setImportResult(result);
      setImportLoading(false);
      alert(`Import complete!\n\n${totalChunks > 1 ? `Uploaded in ${totalChunks} chunks.\n` : ''}Upserted: ${totalUpserted}\nSkipped: ${totalSkipped}\nErrors: ${totalErrors}`);
    } catch (error) {
      setImportLoading(false);
      alert('Import failed: ' + (error.response?.data?.detail || error.message));
    }
    event.target.value = ''; // reset file input
  };

  // ── Bulk delete questions from JSON file of IDs ──
  const handleBulkDelete = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const ids = JSON.parse(text);

      if (!Array.isArray(ids) || ids.length === 0) {
        alert('Invalid file: expected a JSON array of question IDs.');
        event.target.value = '';
        return;
      }

      if (!window.confirm(`Delete ${ids.length} questions?\n\nThis is a soft delete — questions can be restored later.`)) {
        event.target.value = '';
        return;
      }

      setDeleteLoading(true);
      const response = await bulkDeleteQuestions(ids);
      setDeleteLoading(false);
      alert(`Delete complete!\n\nDeleted: ${response.data.deleted_count}\nRequested: ${response.data.requested}`);
    } catch (error) {
      setDeleteLoading(false);
      alert('Delete failed: ' + (error.response?.data?.detail || error.message));
    }
    event.target.value = '';
  };

  const fetchSubscriptionHistory = async (userId) => {
    setLoadingHistory(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/user/${userId}/subscription-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionHistory(response.data.history || []);
    } catch (error) {
      console.error('Error fetching subscription history:', error);
      setSubscriptionHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleGrantFreeAccess = async (userId, plan) => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${BACKEND_URL}/api/admin/grant-access`, {
        user_id: userId,
        subscription_plan: plan,
        duration_days: plan === 'annual' ? 365 : plan === 'quarterly' ? 90 : plan === 'monthly' ? 30 : 7
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Free access granted successfully!');
      fetchUsers();
      fetchSubscriptionHistory(userId);
    } catch (error) {
      console.error('Error granting access:', error);
      alert('Failed to grant access: ' + (error.response?.data?.detail || error.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevokeAccess = async (userId) => {
    if (!window.confirm('Are you sure you want to revoke this user\'s subscription?')) return;
    
    setActionLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${BACKEND_URL}/api/admin/revoke-access`, {
        user_id: userId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Access revoked successfully!');
      fetchUsers();
      fetchSubscriptionHistory(userId);
    } catch (error) {
      console.error('Error revoking access:', error);
      alert('Failed to revoke access: ' + (error.response?.data?.detail || error.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleAdmin = async (userId, makeAdmin) => {
    if (!window.confirm(`Are you sure you want to ${makeAdmin ? 'grant' : 'revoke'} admin access?`)) return;
    
    setActionLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${BACKEND_URL}/api/admin/toggle-admin`, {
        user_id: userId,
        is_admin: makeAdmin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(`Admin access ${makeAdmin ? 'granted' : 'revoked'} successfully!`);
      fetchUsers();
      setShowUserModal(false);
    } catch (error) {
      console.error('Error toggling admin:', error);
      alert('Failed to toggle admin: ' + (error.response?.data?.detail || error.message));
    } finally {
      setActionLoading(false);
    }
  };

  // ── User Deactivation/Reactivation ──

  const handleDeactivate = async () => {
    setDeactivateLoading(true);
    try {
      await deactivateUser(selectedUser.id);
      alert(`${selectedUser.full_name || selectedUser.email} has been deactivated. They can no longer log in.`);
      setShowDeactivateConfirm(false);
      setShowUserModal(false);
      fetchUsers();
    } catch (error) {
      alert('Failed to deactivate: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDeactivateLoading(false);
    }
  };

  const handleReactivate = async () => {
    setDeactivateLoading(true);
    try {
      await reactivateUser(selectedUser.id);
      alert(`${selectedUser.full_name || selectedUser.email} has been reactivated. They can now log in again.`);
      setShowUserModal(false);
      fetchUsers();
    } catch (error) {
      alert('Failed to reactivate: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDeactivateLoading(false);
    }
  };

  // ── User Merge ──

  const mergeFilteredUsers = users.filter(u => {
    if (!mergeSearchTerm.trim()) return false;
    if (u.id === selectedUser?.id) return false; // don't show the same user
    const term = mergeSearchTerm.toLowerCase();
    return u.email?.toLowerCase().includes(term) || u.full_name?.toLowerCase().includes(term);
  });

  const handleMergeConfirm = async () => {
    setMergeLoading(true);
    try {
      const response = await mergeUsers({
        primary_user_id: selectedUser.id,
        secondary_user_id: mergeTargetUser.id
      });
      setMergeResult(response.data);
      setShowMergeConfirm(false);
    } catch (error) {
      alert('Merge failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setMergeLoading(false);
    }
  };

  const handleCloseMerge = () => {
    setShowMergeModal(false);
    setMergeSearchTerm('');
    setMergeTargetUser(null);
    setShowMergeConfirm(false);
    setMergeResult(null);
    setShowUserModal(false);
    fetchUsers();
  };

  const handleUserClick = (user) => {
    setSelectedUser(user);
    setShowUserModal(true);
    fetchSubscriptionHistory(user.id);
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.full_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filterSubscription === 'all') return matchesSearch;
    if (filterSubscription === 'admin') return matchesSearch && user.is_admin;
    if (filterSubscription === 'free') return matchesSearch && (!user.subscription_plan || user.subscription_plan === 'free' || user.subscription_status === 'free');
    if (['explorer', 'essential', 'professional', 'elite'].includes(filterSubscription)) {
      return matchesSearch && getBaseTier(user.subscription_plan) === filterSubscription;
    }
    if (filterSubscription === 'free_grant') return matchesSearch && user.subscription_status === 'free_grant';
    if (filterSubscription === 'active') return matchesSearch && user.subscription_status === 'active';
    if (filterSubscription === 'deactivated') return matchesSearch && user.is_active === false;

    return matchesSearch;
  });

  const getSubscriptionBadge = (user) => {
    const plan = user.subscription_plan || 'free';
    const status = user.subscription_status || 'free';
    const { tier } = parsePlan(plan);
    const tierLabel = getTierName(plan);

    const tierColors = {
      'elite': 'bg-amber-100 text-amber-800 border-amber-300',
      'professional': 'bg-purple-100 text-purple-800 border-purple-300',
      'essential': 'bg-cyan-100 text-cyan-800 border-cyan-300',
      'explorer': 'bg-blue-100 text-blue-800 border-blue-300',
      'free': 'bg-gray-100 text-gray-600 border-gray-300'
    };

    const color = tierColors[tier] || tierColors.free;

    return (
      <div className="flex flex-col gap-1">
        <span className={`px-2 py-1 ${color} border rounded-full text-xs font-bold`}>
          {tierLabel}
        </span>
        {status === 'free_grant' && (
          <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded">Granted</span>
        )}
        {status === 'active' && (
          <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">Paid</span>
        )}
      </div>
    );
  };

  const formatHistoryAction = (item) => {
    const date = new Date(item.timestamp).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
    
    if (item.action === 'registration') {
      return { text: 'Registered as free user', date, color: 'bg-blue-100 text-blue-700' };
    }
    if (item.action === 'upgrade') {
      return { 
        text: `Upgraded from ${item.from_plan || 'free'} to ${item.to_plan}`, 
        date, 
        color: 'bg-green-100 text-green-700',
        extra: item.grant_type === 'free_grant' ? '(Free Grant)' : ''
      };
    }
    if (item.action === 'revoke') {
      return { text: `Revoked ${item.from_plan} subscription`, date, color: 'bg-red-100 text-red-700' };
    }
    return { text: item.action, date, color: 'bg-gray-100 text-gray-700' };
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <div className="bg-gradient-to-r from-cyan-600 to-cyan-400 text-white rounded-lg p-8 mb-6">
          <h1 className="text-4xl font-bold mb-2">Admin Dashboard</h1>
          <p className="text-lg opacity-90">Manage users, subscriptions, and monitor platform usage</p>
        </div>

        {/* Admin Navigation */}
        <div className="flex gap-3 mb-6 flex-wrap">
          <button
            onClick={() => navigate('/admin/analytics')}
            className="flex items-center gap-3 px-4 py-3 text-white rounded-xl font-medium transition-all"
            style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', boxShadow: 'var(--shadow-3d-btn)' }}
          >
            <Icon3D icon={BarChart3} colour="turquoise" size="sm" />
            <span>Analytics Dashboard</span>
          </button>
          <button
            onClick={() => navigate('/admin/email')}
            className="flex items-center gap-3 px-4 py-3 text-white rounded-xl font-medium transition-all"
            style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', boxShadow: 'var(--shadow-3d-btn)' }}
          >
            <Icon3D icon={Mail} colour="turquoise" size="sm" />
            <span>Email Manager</span>
          </button>
          <button
            onClick={() => navigate('/admin/reported-issues')}
            className="flex items-center gap-3 px-4 py-3 text-white rounded-xl font-medium transition-all"
            style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', boxShadow: 'var(--shadow-3d-btn)' }}
          >
            <Icon3D icon={Flag} colour="turquoise" size="sm" />
            <span>Reported Issues</span>
          </button>
          <button
            onClick={() => {
              const el = document.getElementById('diagnostic-reports-section');
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            className="flex items-center gap-3 px-4 py-3 text-white rounded-xl font-medium transition-all"
            style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', boxShadow: 'var(--shadow-3d-btn)' }}
          >
            <Icon3D icon={Brain} colour="turquoise" size="sm" />
            <span>Diagnostics Report</span>
          </button>
          <button
            onClick={() => {
              const el = document.getElementById('question-fixes-section');
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            className="flex items-center gap-3 px-4 py-3 text-white rounded-xl font-medium transition-all"
            style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', boxShadow: 'var(--shadow-3d-btn)' }}
          >
            <Icon3D icon={Wrench} colour="turquoise" size="sm" />
            <span>Question Fixes</span>
          </button>
          <button
            onClick={() => navigate('/admin/questions')}
            className="flex items-center gap-3 px-4 py-3 text-white rounded-xl font-medium transition-all"
            style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', boxShadow: 'var(--shadow-3d-btn)' }}
          >
            <Icon3D icon={FileText} colour="turquoise" size="sm" />
            <span>Manage Questions</span>
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
            <div className="bg-white rounded-lg p-6" style={{ boxShadow: 'var(--shadow-card)' }}>
              <div className="text-3xl font-bold text-cyan-600">{stats.total_users}</div>
              <div className="text-gray-600">Total Users</div>
            </div>
            <div className="bg-white rounded-lg p-6" style={{ boxShadow: 'var(--shadow-card)' }}>
              <div className="text-3xl font-bold text-gray-600">
                {users.filter(u => !u.subscription_plan || u.subscription_plan === 'free' || u.subscription_status === 'free').length}
              </div>
              <div className="text-gray-600">Free Users</div>
            </div>
            <div className="bg-white rounded-lg p-6" style={{ boxShadow: 'var(--shadow-card)' }}>
              <div className="text-3xl font-bold text-green-600">{stats.active_subscriptions}</div>
              <div className="text-gray-600">Paid Subscriptions</div>
            </div>
            <div className="bg-white rounded-lg p-6" style={{ boxShadow: 'var(--shadow-card)' }}>
              <div className="text-3xl font-bold text-cyan-600">{stats.free_grants}</div>
              <div className="text-gray-600">Free Grants</div>
            </div>
            <div className="bg-white rounded-lg p-6" style={{ boxShadow: 'var(--shadow-card)' }}>
              <div className="text-3xl font-bold text-cyan-600">{stats.total_questions_answered}</div>
              <div className="text-gray-600">Questions Answered</div>
            </div>
          </div>
        )}

        {/* Diagnostic Summary */}
        {diagnosticSummary && (
          <div className="bg-white rounded-lg p-6 mb-6" style={{ boxShadow: 'var(--shadow-card)' }}>
            <h2 className="text-lg font-bold text-gray-900 mb-4">Diagnostic Test Completion</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="bg-slate-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-cyan-600">{diagnosticSummary.started_test_1}</div>
                <div className="text-sm text-gray-500">Started Test 1</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-cyan-600">{diagnosticSummary.completed_test_2}</div>
                <div className="text-sm text-gray-500">Completed Test 2</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-emerald-600">{diagnosticSummary.completed_all_3}</div>
                <div className="text-sm text-gray-500">Completed All 3</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-cyan-600">{diagnosticSummary.completion_rate_pct}%</div>
                <div className="text-sm text-gray-500">Completion Rate</div>
              </div>
            </div>
            {diagnosticSummary.users?.length > 0 && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="pb-2">User</th>
                    <th className="pb-2">Tests Done</th>
                    <th className="pb-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {diagnosticSummary.users.map((u, i) => (
                    <tr key={i} className="border-b last:border-0 hover:bg-slate-50">
                      <td className="py-2">
                        <div className="font-medium text-gray-800">{u.full_name || u.email}</div>
                        {u.full_name && <div className="text-xs text-gray-400">{u.email}</div>}
                      </td>
                      <td className="py-2">
                        <div className="flex gap-1">
                          {[1,2,3].map(n => (
                            <span key={n} className={`w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center ${u.tests_completed.includes(n) ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-400'}`}>{n}</span>
                          ))}
                        </div>
                      </td>
                      <td className="py-2">
                        {u.all_complete
                          ? <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">Complete</span>
                          : <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">In Progress</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Diagnostic Reports */}
        <div id="diagnostic-reports-section" className="bg-white rounded-lg p-6 mb-6" style={{ boxShadow: 'var(--shadow-card)' }}>
          <div className="flex items-center gap-3 mb-4">
            <Icon3D icon={Brain} colour="turquoise" size="sm" />
            <h2 className="text-lg font-bold text-gray-900">Diagnostic Reports</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Generate comprehensive diagnostic reports for users with answer history. Reports include accuracy analysis, timing metrics (excluding walked-away answers &gt;4 min), misconception maps, and AI-generated insights.
          </p>

          {reportUsers.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="pb-2 pr-4">User</th>
                    <th className="pb-2 pr-4">Answers</th>
                    <th className="pb-2 pr-4">Last Report</th>
                    <th className="pb-2 pr-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reportUsers.map((u) => (
                    <tr key={u.user_id} className="border-b last:border-0 hover:bg-slate-50">
                      <td className="py-2 pr-4">
                        <div>
                          <span className="font-medium text-gray-900">{u.name}</span>
                          <span className="text-gray-400 text-xs ml-2">{u.email}</span>
                        </div>
                      </td>
                      <td className="py-2 pr-4 text-gray-600">{u.answer_count}</td>
                      <td className="py-2 pr-4 text-gray-500 text-xs">
                        {u.has_report ? (
                          <span className="flex items-center gap-1">
                            <CheckCircle className="w-3 h-3 text-emerald-500" />
                            {new Date(u.last_report_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                            <span className="text-gray-400">({u.last_report_answers} answers)</span>
                          </span>
                        ) : (
                          <span className="text-gray-400">None</span>
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleGenerateReport(u.user_id)}
                            disabled={generatingReport === u.user_id}
                            className={`flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-medium transition-all ${
                              generatingReport === u.user_id
                                ? 'bg-gray-200 text-gray-500'
                                : 'text-white'
                            }`}
                            style={generatingReport !== u.user_id ? { background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', boxShadow: '0 4px 12px rgba(6,182,212,0.3), 0 2px 4px rgba(0,0,0,0.2)' } : {}}
                          >
                            {generatingReport === u.user_id ? (
                              <>
                                <RefreshCw className="w-3 h-3 animate-spin" />
                                Generating...
                              </>
                            ) : (
                              <>
                                <FileText className="w-3 h-3" />
                                {u.has_report ? 'Regenerate' : 'Generate'}
                              </>
                            )}
                          </button>
                          {u.has_report && (
                            <button
                              onClick={() => handleViewReport(u.user_id)}
                              className="flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-medium text-white transition-all"
                              style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', boxShadow: '0 4px 12px rgba(6,182,212,0.3), 0 2px 4px rgba(0,0,0,0.2)' }}
                            >
                              <Eye className="w-3 h-3" />
                              View
                            </button>
                          )}
                          <button
                            onClick={() => handleGenerateAdaptiveReport(u.user_id)}
                            disabled={generatingAdaptive === u.user_id}
                            className={`flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-medium transition-all ${
                              generatingAdaptive === u.user_id
                                ? 'bg-gray-200 text-gray-500'
                                : 'text-white'
                            }`}
                            style={generatingAdaptive !== u.user_id ? { background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', boxShadow: '0 4px 12px rgba(6,182,212,0.3), 0 2px 4px rgba(0,0,0,0.2)' } : {}}
                          >
                            {generatingAdaptive === u.user_id ? (
                              <>
                                <RefreshCw className="w-3 h-3 animate-spin" />
                                Loading...
                              </>
                            ) : (
                              <>
                                <TrendingUp className="w-3 h-3" />
                                Progress
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => handleViewOverallReport(u.user_id)}
                            disabled={loadingOverall === u.user_id}
                            className={`flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-medium transition-all ${
                              loadingOverall === u.user_id ? 'bg-gray-200 text-gray-500' : 'text-white'
                            }`}
                            style={loadingOverall !== u.user_id ? { background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', boxShadow: '0 4px 12px rgba(6,182,212,0.3), 0 2px 4px rgba(0,0,0,0.2)' } : {}}
                          >
                            {loadingOverall === u.user_id ? <><RefreshCw className="w-3 h-3 animate-spin" />Loading...</> : <><FileText className="w-3 h-3" />Overall</>}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-4 text-gray-400">
              <Brain className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No users with enough answer history for reports.</p>
            </div>
          )}
        </div>

        {/* Diagnostic Report Detail Modal */}
        {showReportDetail && activeReport && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto py-8">
            <div className="bg-white rounded-xl w-full max-w-4xl mx-4 shadow-2xl">
              {/* Header */}
              <div className="rounded-t-xl p-6 text-white" style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold">Diagnostic Report</h2>
                    <p className="text-white/80 text-sm mt-1">
                      {activeReport.user_name} ({activeReport.user_email})
                    </p>
                    <p className="text-white/60 text-xs mt-1">
                      {activeReport.total_answers} answers analysed · Generated {new Date(activeReport.generated_at).toLocaleString('en-AU')}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowReportDetail(false)}
                    className="text-white/80 hover:text-white text-2xl font-bold"
                  >
                    ×
                  </button>
                </div>

                {/* Tab navigation */}
                <div className="flex gap-2 mt-4">
                  {['full', '50pct', 'comparison', 'insights'].map(tab => (
                    <button
                      key={tab}
                      onClick={() => setReportSection(tab)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        reportSection === tab
                          ? 'bg-white/20 text-white'
                          : 'text-white/60 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      {tab === 'full' ? 'Full Report' : tab === '50pct' ? '50% Snapshot' : tab === 'comparison' ? 'Comparison' : 'Insights'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Content */}
              <div className="p-6 max-h-[70vh] overflow-y-auto">

                {/* ── Full Report / 50% Snapshot ── */}
                {(reportSection === 'full' || reportSection === '50pct') && (() => {
                  const snap = reportSection === 'full' ? activeReport.snapshot_full : activeReport.snapshot_50pct;
                  if (!snap || !snap.answers_included) {
                    return <p className="text-gray-500 text-center py-8">No data available for this snapshot.</p>;
                  }

                  return (
                    <div className="space-y-6">
                      {/* Summary cards */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="bg-slate-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-gray-500">Questions</p>
                          <p className="text-2xl font-bold text-gray-900">{snap.answers_included}</p>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-gray-500">Accuracy</p>
                          <p className={`text-2xl font-bold ${getAccuracyColor(snap.overall_accuracy)}`}>
                            {formatPct(snap.overall_accuracy)}
                          </p>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-gray-500">Avg Response</p>
                          <p className="text-2xl font-bold text-gray-900">{formatMs(snap.timing?.avg_response_time_ms)}</p>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-gray-500">Walked Away</p>
                          <p className="text-2xl font-bold text-amber-600">{snap.timing?.walked_away_count || 0}</p>
                        </div>
                      </div>

                      {/* Category breakdown */}
                      <div>
                        <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                          <Target className="w-4 h-4 text-cyan-600" />
                          Category Breakdown
                        </h3>
                        <div className="space-y-2">
                          {Object.entries(snap.categories || {})
                            .sort((a, b) => a[1].weakness_rank - b[1].weakness_rank)
                            .map(([catName, cat]) => (
                              <div key={catName} className="border border-gray-200 rounded-lg">
                                <button
                                  onClick={() => toggleCategory(catName)}
                                  className="w-full flex items-center justify-between p-3 hover:bg-slate-50 transition-all"
                                >
                                  <div className="flex items-center gap-3">
                                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${getAccuracyBg(cat.accuracy)}`}>
                                      #{cat.weakness_rank}
                                    </span>
                                    <span className="font-medium text-gray-900 capitalize">{catName}</span>
                                    <span className="text-xs text-gray-400">({cat.total_answered} q)</span>
                                  </div>
                                  <div className="flex items-center gap-4">
                                    <span className={`font-bold ${getAccuracyColor(cat.accuracy)}`}>
                                      {formatPct(cat.accuracy)}
                                    </span>
                                    <span className="text-xs text-gray-400">θ={cat.theta}</span>
                                    {expandedCategories[catName] ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                                  </div>
                                </button>
                                {expandedCategories[catName] && (
                                  <div className="border-t p-3 bg-slate-50 text-sm space-y-2">
                                    <div className="grid grid-cols-3 gap-3">
                                      <div>
                                        <span className="text-gray-500">Theory:</span>
                                        <span className={`ml-2 font-medium ${getAccuracyColor(cat.theory_accuracy)}`}>{formatPct(cat.theory_accuracy)}</span>
                                      </div>
                                      <div>
                                        <span className="text-gray-500">Scenario:</span>
                                        <span className={`ml-2 font-medium ${getAccuracyColor(cat.scenario_accuracy)}`}>{formatPct(cat.scenario_accuracy)}</span>
                                      </div>
                                      <div>
                                        <span className="text-gray-500">Avg Time:</span>
                                        <span className="ml-2 font-medium text-gray-700">{formatMs(cat.avg_response_time_ms)}</span>
                                      </div>
                                    </div>
                                    {cat.walked_away_count > 0 && (
                                      <div className="flex items-center gap-1 text-amber-600 text-xs">
                                        <AlertTriangle className="w-3 h-3" />
                                        {cat.walked_away_count} walked-away answers excluded from timing
                                      </div>
                                    )}
                                    {cat.difficulty_breakdown && Object.keys(cat.difficulty_breakdown).length > 0 && (
                                      <div>
                                        <span className="text-gray-500 text-xs">By difficulty:</span>
                                        <div className="flex gap-2 mt-1">
                                          {Object.entries(cat.difficulty_breakdown).sort((a,b) => a[0].localeCompare(b[0])).map(([level, data]) => (
                                            <span key={level} className="px-2 py-0.5 bg-white rounded text-xs border">
                                              L{level}: {data.correct}/{data.total} ({data.accuracy}%)
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    {cat.sub_category_accuracy && Object.keys(cat.sub_category_accuracy).length > 1 && (
                                      <div>
                                        <span className="text-gray-500 text-xs">Sub-categories:</span>
                                        <div className="flex flex-wrap gap-1 mt-1">
                                          {Object.entries(cat.sub_category_accuracy).sort((a,b) => a[1] - b[1]).map(([sub, acc]) => (
                                            <span key={sub} className={`px-2 py-0.5 rounded text-xs ${getAccuracyBg(acc)}`}>
                                              {sub}: {formatPct(acc)}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                          ))}
                        </div>
                      </div>

                      {/* Behaviour profile */}
                      {snap.behaviour && (
                        <div>
                          <h3 className="font-bold text-gray-900 mb-3">Behaviour Profile</h3>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div className="bg-slate-50 rounded-lg p-3">
                              <p className="text-xs text-gray-500">Fatigue Onset</p>
                              <p className="font-bold text-gray-900">
                                {snap.behaviour.fatigue_onset_position ? `Q${snap.behaviour.fatigue_onset_position}` : 'None detected'}
                              </p>
                            </div>
                            <div className="bg-slate-50 rounded-lg p-3">
                              <p className="text-xs text-gray-500">Fast-Wrong Ratio</p>
                              <p className="font-bold text-gray-900">{formatPct(snap.behaviour.fast_wrong_ratio)}</p>
                            </div>
                            <div className="bg-slate-50 rounded-lg p-3">
                              <p className="text-xs text-gray-500">Working Memory</p>
                              <p className="font-bold text-gray-900 capitalize">{snap.behaviour.working_memory_indicator}</p>
                            </div>
                            <div className="bg-slate-50 rounded-lg p-3">
                              <p className="text-xs text-gray-500">Optimal Session</p>
                              <p className="font-bold text-gray-900">{snap.behaviour.optimal_session_length} questions</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Misconceptions */}
                      {snap.misconception_map?.dominant_error_type && (
                        <div>
                          <h3 className="font-bold text-gray-900 mb-3">Misconception Map</h3>
                          <div className="space-y-2">
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                              <p className="text-sm font-medium text-red-800">
                                Dominant error type: <span className="font-bold">{snap.misconception_map.dominant_error_type.replace(/_/g, ' ')}</span>
                              </p>
                              {snap.misconception_map.cross_category_pattern && (
                                <p className="text-xs text-red-600 mt-1">
                                  Cross-category pattern: {snap.misconception_map.cross_category_pattern.replace(/_/g, ' ')} (appears in 3+ categories)
                                </p>
                              )}
                            </div>
                            {snap.misconception_map.confident_wrong_categories?.length > 0 && (
                              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                                <p className="text-sm text-amber-800">
                                  <strong>Confident-wrong</strong> answers concentrated in:{' '}
                                  {snap.misconception_map.confident_wrong_categories.map(c => c.charAt(0).toUpperCase() + c.slice(1)).join(', ')}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* ── Comparison Tab ── */}
                {reportSection === 'comparison' && (
                  <div className="space-y-4">
                    <div className="bg-cyan-50 rounded-lg p-4 mb-4">
                      <p className="text-sm text-gray-700">
                        Comparing <strong>first {activeReport.snapshot_50pct?.answers_included || 0} answers</strong> (50%)
                        with <strong>all {activeReport.snapshot_full?.answers_included || 0} answers</strong> (100%).
                        This shows how performance changed as more questions were answered.
                      </p>
                    </div>

                    {(activeReport.comparisons || []).length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-gray-500 border-b">
                              <th className="pb-2 pr-4">Metric</th>
                              <th className="pb-2 pr-4 text-center">50%</th>
                              <th className="pb-2 pr-4 text-center">100%</th>
                              <th className="pb-2 pr-4">Change</th>
                            </tr>
                          </thead>
                          <tbody>
                            {activeReport.comparisons.map((comp, i) => (
                              <tr key={i} className="border-b last:border-0 hover:bg-slate-50">
                                <td className="py-2 pr-4 font-medium text-gray-900">{comp.label}</td>
                                <td className="py-2 pr-4 text-center text-gray-600">
                                  {comp.value_at_50pct !== null && comp.value_at_50pct !== undefined
                                    ? (typeof comp.value_at_50pct === 'number' && comp.value_at_50pct < 1 && comp.value_at_50pct > -1
                                        ? formatPct(comp.value_at_50pct)
                                        : String(comp.value_at_50pct))
                                    : '—'}
                                </td>
                                <td className="py-2 pr-4 text-center text-gray-600">
                                  {comp.value_at_100pct !== null && comp.value_at_100pct !== undefined
                                    ? (typeof comp.value_at_100pct === 'number' && comp.value_at_100pct < 1 && comp.value_at_100pct > -1
                                        ? formatPct(comp.value_at_100pct)
                                        : String(comp.value_at_100pct))
                                    : '—'}
                                </td>
                                <td className="py-2 pr-4">
                                  <span className={`text-xs font-medium ${
                                    comp.interpretation?.includes('Improved') || comp.interpretation?.includes('Faster')
                                      ? 'text-emerald-600'
                                      : comp.interpretation?.includes('Declined') || comp.interpretation?.includes('Slower')
                                        ? 'text-red-600'
                                        : 'text-gray-500'
                                  }`}>
                                    {comp.interpretation?.includes('Improved') || comp.interpretation?.includes('Faster') ? (
                                      <span className="flex items-center gap-1"><TrendingUp className="w-3 h-3" /> {comp.interpretation}</span>
                                    ) : comp.interpretation?.includes('Declined') || comp.interpretation?.includes('Slower') ? (
                                      <span className="flex items-center gap-1"><TrendingDown className="w-3 h-3" /> {comp.interpretation}</span>
                                    ) : (
                                      <span className="flex items-center gap-1"><Minus className="w-3 h-3" /> {comp.interpretation || 'No change'}</span>
                                    )}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-8">No comparison data available.</p>
                    )}
                  </div>
                )}

                {/* ── Insights Tab ── */}
                {reportSection === 'insights' && (
                  <div className="space-y-6">
                    {/* Key Findings */}
                    {activeReport.key_findings?.length > 0 && (
                      <div>
                        <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                          <Target className="w-4 h-4 text-cyan-600" />
                          Key Findings
                        </h3>
                        <div className="space-y-2">
                          {activeReport.key_findings.map((finding, i) => (
                            <div key={i} className="bg-cyan-50 border border-cyan-200 rounded-lg p-3 text-sm text-cyan-900">
                              {finding}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Improvement Strategies */}
                    {activeReport.improvement_strategies?.length > 0 && (
                      <div>
                        <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                          <TrendingUp className="w-4 h-4 text-emerald-600" />
                          Improvement Strategies
                        </h3>
                        <div className="space-y-2">
                          {activeReport.improvement_strategies.map((strategy, i) => (
                            <div key={i} className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-900">
                              {strategy}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Non-obvious Patterns */}
                    {activeReport.non_obvious_patterns?.length > 0 && (
                      <div>
                        <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                          <Brain className="w-4 h-4 text-cyan-600" />
                          Non-Obvious Patterns
                        </h3>
                        <div className="space-y-2">
                          {activeReport.non_obvious_patterns.map((pattern, i) => (
                            <div key={i} className="bg-cyan-50 border border-cyan-200 rounded-lg p-3 text-sm text-cyan-900">
                              {pattern}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Agent Learning Notes */}
                    {activeReport.agent_learning_notes?.length > 0 && (
                      <div>
                        <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                          <Settings className="w-4 h-4 text-gray-500" />
                          Agent Learning Notes
                        </h3>
                        <div className="space-y-2">
                          {activeReport.agent_learning_notes.map((note, i) => (
                            <div key={i} className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-700">
                              {note}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="border-t p-4 flex justify-between">
                <button
                  onClick={() => window.print()}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white transition-all"
                  style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', boxShadow: '0 4px 12px rgba(6,182,212,0.3), 0 2px 4px rgba(0,0,0,0.2)' }}
                >
                  <Printer className="w-4 h-4" />
                  Print Report
                </button>
                <button
                  onClick={() => setShowReportDetail(false)}
                  className="px-4 py-2 rounded-lg font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Adaptive Progress Report Modal */}
        {showAdaptiveDetail && activeAdaptiveReport && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto py-8">
            <div className="bg-white rounded-xl w-full max-w-4xl mx-4 shadow-2xl">
              {/* Header */}
              <div className="rounded-t-xl p-6 text-white" style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold">Adaptive Progress Report</h2>
                    <p className="text-white/80 text-sm mt-1">
                      {activeAdaptiveReport.user_name} ({activeAdaptiveReport.user_email})
                    </p>
                    <p className="text-white/60 text-xs mt-1">
                      {activeAdaptiveReport.total_practice_questions} practice questions · Generated {new Date(activeAdaptiveReport.generated_at).toLocaleString('en-AU')}
                    </p>
                  </div>
                  <button onClick={() => setShowAdaptiveDetail(false)} className="text-white/80 hover:text-white text-2xl">×</button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Diagnostic Baseline */}
                <div>
                  <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Target className="w-4 h-4 text-cyan-600" />
                    Diagnostic Baseline
                  </h3>
                  <div className="bg-cyan-50 rounded-lg p-4 mb-3">
                    <div className="text-lg font-bold text-cyan-700">
                      {activeAdaptiveReport.diagnostic_baseline?.overall_accuracy}% overall accuracy
                    </div>
                    <div className="text-sm text-gray-600">
                      {activeAdaptiveReport.diagnostic_baseline?.total_questions} diagnostic questions
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {Object.entries(activeAdaptiveReport.diagnostic_baseline?.categories || {}).map(([cat, data]) => (
                      <div key={cat} className="bg-slate-50 rounded-lg p-3">
                        <div className="text-xs font-medium text-gray-500 capitalize">{cat}</div>
                        <div className="text-sm font-bold text-gray-900">{data.accuracy}%</div>
                        <div className="text-xs text-gray-400">{data.correct}/{data.total}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Progress Snapshots */}
                {activeAdaptiveReport.practice_snapshots?.length > 0 ? (
                  <div>
                    <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-cyan-600" />
                      Practice Progress (every 75 questions)
                    </h3>
                    <div className="space-y-4">
                      {activeAdaptiveReport.practice_snapshots.map((snap, idx) => (
                        <div key={idx} className="border rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="font-medium text-gray-900">
                              After {snap.questions_completed} questions
                            </div>
                            <div className="text-sm font-bold text-cyan-600">
                              {snap.overall_accuracy}% overall
                            </div>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            {Object.entries(snap.categories || {}).map(([cat, data]) => (
                              <div key={cat} className="bg-slate-50 rounded-lg p-2">
                                <div className="text-xs font-medium text-gray-500 capitalize">{cat}</div>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-bold text-gray-900">{data.accuracy}%</span>
                                  <span className={`text-xs font-medium ${
                                    data.delta > 0 ? 'text-emerald-600' : data.delta < 0 ? 'text-red-600' : 'text-gray-400'
                                  }`}>
                                    {data.delta > 0 ? '+' : ''}{data.delta}%
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    <TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No practice questions yet. The student needs to complete at least 75 practice questions for the first snapshot.</p>
                  </div>
                )}

                {/* Improving / Declining */}
                {(activeAdaptiveReport.improving_categories?.length > 0 || activeAdaptiveReport.declining_categories?.length > 0) && (
                  <div className="grid grid-cols-2 gap-4">
                    {activeAdaptiveReport.improving_categories?.length > 0 && (
                      <div className="bg-emerald-50 rounded-lg p-4">
                        <h4 className="font-medium text-emerald-800 mb-2 flex items-center gap-1">
                          <TrendingUp className="w-4 h-4" /> Improving
                        </h4>
                        <div className="space-y-1">
                          {activeAdaptiveReport.improving_categories.map(cat => (
                            <div key={cat} className="text-sm text-emerald-700 capitalize">{cat}</div>
                          ))}
                        </div>
                      </div>
                    )}
                    {activeAdaptiveReport.declining_categories?.length > 0 && (
                      <div className="bg-red-50 rounded-lg p-4">
                        <h4 className="font-medium text-red-800 mb-2 flex items-center gap-1">
                          <TrendingDown className="w-4 h-4" /> Needs Attention
                        </h4>
                        <div className="space-y-1">
                          {activeAdaptiveReport.declining_categories.map(cat => (
                            <div key={cat} className="text-sm text-red-700 capitalize">{cat}</div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="border-t p-4 flex justify-end">
                <button
                  onClick={() => setShowAdaptiveDetail(false)}
                  className="px-4 py-2 rounded-lg font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Overall Report Modal */}
        {showOverallDetail && activeOverallReport && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto py-8">
            <div className="bg-white rounded-xl w-full max-w-4xl mx-4 shadow-2xl">
              <div className="rounded-t-xl p-6 text-white" style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold">Overall Learning Report</h2>
                    <p className="text-white/80 text-sm mt-1">{activeOverallReport.user_name} ({activeOverallReport.user_email})</p>
                    <p className="text-white/60 text-xs mt-1">{activeOverallReport.totals?.total_answered} total answers · Generated {new Date(activeOverallReport.generated_at).toLocaleString('en-AU')}</p>
                  </div>
                  <button onClick={() => setShowOverallDetail(false)} className="text-white/80 hover:text-white text-2xl">×</button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Headline totals */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-cyan-50 rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-cyan-700">{activeOverallReport.totals?.overall_accuracy_pct}%</div>
                    <div className="text-sm text-gray-600">Overall Accuracy</div>
                    <div className="text-xs text-gray-400">{activeOverallReport.totals?.total_correct}/{activeOverallReport.totals?.total_answered}</div>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-gray-700">{activeOverallReport.totals?.diagnostic_questions}</div>
                    <div className="text-sm text-gray-600">Diagnostic Qs</div>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-gray-700">{activeOverallReport.totals?.practice_questions}</div>
                    <div className="text-sm text-gray-600">Practice Qs</div>
                  </div>
                </div>

                {/* Pre vs Post Diagnostic */}
                {activeOverallReport.pre_post_comparison && (
                  <div>
                    <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-cyan-600" />
                      Pre vs Post Diagnostic Comparison
                    </h3>
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div className="bg-slate-50 rounded-xl p-4 text-center">
                        <div className="text-xl font-bold text-gray-700">{activeOverallReport.pre_post_comparison.diagnostic_accuracy_pct ?? '—'}%</div>
                        <div className="text-sm text-gray-500">Baseline (Before Adaptive Learning)</div>
                        {activeOverallReport.totals?.diagnostic_questions > 0 && (
                          <div className="text-xs text-gray-400 mt-1">{activeOverallReport.totals.diagnostic_questions} questions</div>
                        )}
                      </div>
                      <div className={`rounded-xl p-4 text-center ${activeOverallReport.pre_post_comparison.overall_improvement > 0 ? 'bg-emerald-50' : activeOverallReport.pre_post_comparison.overall_improvement < 0 ? 'bg-red-50' : 'bg-slate-50'}`}>
                        <div className={`text-xl font-bold ${activeOverallReport.pre_post_comparison.overall_improvement > 0 ? 'text-emerald-700' : activeOverallReport.pre_post_comparison.overall_improvement < 0 ? 'text-red-700' : 'text-gray-700'}`}>
                          {activeOverallReport.pre_post_comparison.practice_accuracy_pct ?? '—'}%
                          {activeOverallReport.pre_post_comparison.overall_improvement != null && (
                            <span className="text-sm ml-1">({activeOverallReport.pre_post_comparison.overall_improvement > 0 ? '+' : ''}{activeOverallReport.pre_post_comparison.overall_improvement}%)</span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">After Adaptive Learning</div>
                        {activeOverallReport.totals?.practice_questions > 0 && (
                          <div className="text-xs text-gray-400 mt-1">{activeOverallReport.totals.practice_questions} questions</div>
                        )}
                      </div>
                    </div>
                    {activeOverallReport.pre_post_comparison.diagnostic_wrong_total > 0 && (
                      <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-4">
                        <div className="font-medium text-cyan-800 mb-1">Learning Detection</div>
                        <p className="text-sm text-cyan-700">
                          <strong>{activeOverallReport.pre_post_comparison.questions_corrected_after_diagnostic}</strong> of{' '}
                          <strong>{activeOverallReport.pre_post_comparison.diagnostic_wrong_total}</strong> questions answered incorrectly during diagnostics were later answered correctly after adaptive learning began.
                          {activeOverallReport.pre_post_comparison.learning_rate_pct != null && (
                            <span className="font-bold"> ({activeOverallReport.pre_post_comparison.learning_rate_pct}% correction rate)</span>
                          )}
                          {' '}— this confirms the adaptive engine is directing study toward areas that needed improvement.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Improvement Over Time */}
                {activeOverallReport.time_snapshots?.length > 1 && (
                  <div>
                    <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-cyan-600" />
                      Accuracy Over Time (every 50 questions)
                    </h3>
                    <div className="flex items-end gap-1 h-24 bg-slate-50 rounded-xl p-4">
                      {activeOverallReport.time_snapshots.map((snap, i) => {
                        const h = Math.max(snap.window_accuracy, 2);
                        const color = snap.window_accuracy >= 70 ? '#10b981' : snap.window_accuracy >= 50 ? '#f59e0b' : '#ef4444';
                        return (
                          <div key={i} className="flex-1 flex flex-col items-center gap-1" title={`Q${snap.questions_completed}: ${snap.window_accuracy}%`}>
                            <div className="text-xs text-gray-400" style={{ fontSize: '9px' }}>{snap.window_accuracy}%</div>
                            <div className="w-full rounded-t" style={{ height: `${h * 0.6}px`, backgroundColor: color, minHeight: '2px' }} />
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Each bar = accuracy of the last 50 questions answered</p>
                  </div>
                )}

                {/* Category Breakdown */}
                {activeOverallReport.category_breakdown?.length > 0 && (
                  <div>
                    <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <Target className="w-4 h-4 text-cyan-600" />
                      Category Breakdown (All Questions)
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-gray-500 border-b text-xs">
                            <th className="pb-2 pr-3">Category</th>
                            <th className="pb-2 pr-3 text-center">Total</th>
                            <th className="pb-2 pr-3 text-center">Overall %</th>
                            <th className="pb-2 pr-3 text-center">Diagnostic</th>
                            <th className="pb-2 text-center">Practice</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activeOverallReport.category_breakdown.map((cat, i) => (
                            <tr key={i} className="border-b border-gray-100 last:border-0">
                              <td className="py-2 pr-3 font-medium text-gray-800 capitalize">{cat.category}</td>
                              <td className="py-2 pr-3 text-center text-gray-500 text-xs">{cat.correct}/{cat.total}</td>
                              <td className="py-2 pr-3 text-center font-bold" style={{ color: cat.accuracy_pct >= 70 ? '#10b981' : cat.accuracy_pct >= 50 ? '#f59e0b' : '#ef4444' }}>{cat.accuracy_pct}%</td>
                              <td className="py-2 pr-3 text-center text-gray-600">{cat.diagnostic_accuracy_pct != null ? `${cat.diagnostic_accuracy_pct}%` : '—'}</td>
                              <td className="py-2 text-center">
                                {cat.practice_accuracy_pct != null ? (
                                  <span className={`font-medium ${cat.improvement_delta > 0 ? 'text-emerald-600' : cat.improvement_delta < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                                    {cat.practice_accuracy_pct}%
                                    {cat.improvement_delta != null && <span className="text-xs ml-1">({cat.improvement_delta > 0 ? '+' : ''}{cat.improvement_delta}%)</span>}
                                  </span>
                                ) : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              <div className="border-t p-4 flex justify-between">
                <button
                  onClick={() => window.print()}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white"
                  style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', boxShadow: '0 4px 12px rgba(6,182,212,0.3), 0 2px 4px rgba(0,0,0,0.2)' }}
                >
                  <Printer className="w-4 h-4" />Print
                </button>
                <button onClick={() => setShowOverallDetail(false)} className="px-4 py-2 rounded-lg font-medium bg-gray-100 text-gray-700 hover:bg-gray-200">Close</button>
              </div>
            </div>
          </div>
        )}

        {/* MCQ Database Sync */}
        <div className="bg-white rounded-lg p-6 mb-6" style={{ boxShadow: 'var(--shadow-card)' }}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">MCQ Database Sync</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowSyncSettings(!showSyncSettings)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg font-medium text-gray-600 hover:bg-gray-100 transition-all"
              >
                <Settings className="w-4 h-4" />
                Settings
              </button>
              <button
                onClick={handleCheckFile}
                disabled={fileCheckLoading}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg font-medium text-gray-600 hover:bg-gray-100 transition-all"
                title="Check if mcq_database.xlsx exists on the server"
              >
                {fileCheckLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                Check File
              </button>
              <button
                onClick={handleLocalReseed}
                disabled={reseedLoading}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  reseedLoading
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : 'text-white'
                }`}
                style={!reseedLoading ? {
                  background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                  boxShadow: '0 4px 12px rgba(6, 182, 212, 0.35), 0 2px 4px rgba(6, 182, 212, 0.2), inset 0 1px 0 rgba(255,255,255,0.2)',
                } : {}}
                title="Re-import all questions from mcq_database.xlsx on the server (no GitHub token needed)"
              >
                <RefreshCw className={`w-4 h-4 ${reseedLoading ? 'animate-spin' : ''}`} />
                {reseedLoading ? 'Re-seeding...' : 'Re-seed from File'}
              </button>
              <button
                onClick={handleExportQuestions}
                disabled={exportLoading}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg font-medium transition-all ${exportLoading ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'text-white'}`}
                style={!exportLoading ? { background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', boxShadow: '0 4px 12px rgba(16,185,129,0.35), 0 2px 4px rgba(16,185,129,0.2), inset 0 1px 0 rgba(255,255,255,0.2)' } : {}}
                title="Download all questions as a file to your PC"
              >
                {exportLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {exportLoading ? 'Exporting...' : 'Export Questions'}
              </button>
              <label
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg font-medium cursor-pointer transition-all ${
                  importLoading
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : 'text-white'
                }`}
                style={!importLoading ? { background: 'linear-gradient(135deg, #22d3ee 0%, #0891b2 100%)', boxShadow: '0 4px 12px rgba(8,145,178,0.35), 0 2px 4px rgba(8,145,178,0.2), inset 0 1px 0 rgba(255,255,255,0.2)' } : {}}
                title="Upload a cleaned question file back to the database"
              >
                {importLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {importLoading ? (importResult?.progress || 'Importing...') : 'Import Questions'}
                <input
                  type="file"
                  accept=".ndjson,.json"
                  onChange={handleImportQuestions}
                  disabled={importLoading}
                  className="hidden"
                />
              </label>
              <label
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg font-medium cursor-pointer transition-all ${
                  deleteLoading
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : 'text-white'
                }`}
                style={!deleteLoading ? { background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', boxShadow: '0 4px 12px rgba(239,68,68,0.35), 0 2px 4px rgba(239,68,68,0.2), inset 0 1px 0 rgba(255,255,255,0.2)' } : {}}
                title="Upload a JSON file of question IDs to delete"
              >
                {deleteLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {deleteLoading ? 'Deleting...' : 'Delete Questions'}
                <input
                  type="file"
                  accept=".json"
                  onChange={handleBulkDelete}
                  disabled={deleteLoading}
                  className="hidden"
                />
              </label>
              <button
                onClick={handleTriggerSync}
                disabled={syncLoading || syncInProgress || !syncConfigSet}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  syncLoading || syncInProgress || !syncConfigSet
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : 'text-white'
                }`}
                style={!(syncLoading || syncInProgress || !syncConfigSet) ? { background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', boxShadow: 'var(--shadow-3d-btn)' } : {}}
                title={!syncConfigSet ? 'Configure sync settings first' : ''}
              >
                <RefreshCw className={`w-4 h-4 ${syncLoading ? 'animate-spin' : ''}`} />
                {syncLoading ? 'Syncing...' : 'Sync Now'}
              </button>
            </div>
          </div>

          {/* File check result banner */}
          {fileCheckResult && (
            <div className={`rounded-lg p-3 mb-4 border ${
              fileCheckResult.status === 'found'
                ? 'bg-emerald-50 border-emerald-200'
                : 'bg-red-50 border-red-200'
            }`}>
              {fileCheckResult.status === 'found' ? (
                <>
                  <p className="text-sm font-medium text-emerald-800">
                    ✅ {fileCheckResult.file_count
                      ? `${fileCheckResult.file_count} file(s) found — ${fileCheckResult.total_size_mb} MB total`
                      : `File found: ${fileCheckResult.file_path}`}
                  </p>
                  {fileCheckResult.files ? (
                    <div className="text-xs text-emerald-600 mt-1">
                      {fileCheckResult.files.map((f, i) => (
                        <div key={i}>{f.path} ({f.size_mb} MB)</div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-emerald-600 mt-1">
                      Size: {fileCheckResult.size_mb} MB | ROOT_DIR: {fileCheckResult.root_dir}
                    </p>
                  )}
                </>
              ) : fileCheckResult.status === 'not_found' ? (
                <>
                  <p className="text-sm font-medium text-red-800">
                    ❌ mcq_database.xlsx NOT found on server
                  </p>
                  <p className="text-xs text-red-600 mt-1">
                    ROOT_DIR: {fileCheckResult.root_dir}
                  </p>
                  <p className="text-xs text-red-500 mt-1">
                    Checked: {fileCheckResult.paths_checked?.map(p => p.path).join(', ')}
                  </p>
                  {fileCheckResult.root_parent_files?.length > 0 && (
                    <p className="text-xs text-red-500 mt-1">
                      Files in project root: {fileCheckResult.root_parent_files?.join(', ')}
                    </p>
                  )}
                </>
              ) : (
                <p className="text-sm font-medium text-red-800">Error: {fileCheckResult.error}</p>
              )}
            </div>
          )}

          {/* Re-seed result banner */}
          {reseedResult && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4">
              <p className="text-sm font-medium text-emerald-800">
                ✅ Re-seed complete: {reseedResult.inserted} inserted, {reseedResult.updated} updated, {reseedResult.unchanged} unchanged, {reseedResult.skipped} skipped
              </p>
              <p className="text-xs text-emerald-600 mt-1">Total questions processed: {reseedResult.imported} of {reseedResult.total} in file</p>
            </div>
          )}

          {/* Config status banner */}
          {!syncConfigSet && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-center gap-2">
              <Settings className="w-4 h-4 text-amber-600 flex-shrink-0" />
              <span className="text-sm text-amber-800">
                GitHub sync not configured. Use <strong>Re-seed from File</strong> instead, or click <strong>Settings</strong> to add your GitHub token.
              </span>
            </div>
          )}

          {/* Settings panel */}
          {showSyncSettings && (
            <div className="bg-slate-50 rounded-lg p-4 mb-4 border border-slate-200">
              <h3 className="font-bold text-gray-800 mb-3">Sync Configuration</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sync Secret</label>
                  <div className="flex gap-2">
                    <input
                      type={showSecretValues ? 'text' : 'password'}
                      value={syncSecret}
                      onChange={(e) => setSyncSecret(e.target.value)}
                      placeholder={syncConfigSet ? '(already set — paste new value to update)' : 'Paste your sync secret here'}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Must match the MCQ_SYNC_SECRET in your GitHub repo secrets</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">GitHub Personal Access Token</label>
                  <input
                    type={showSecretValues ? 'text' : 'password'}
                    value={githubToken}
                    onChange={(e) => setGithubToken(e.target.value)}
                    placeholder={syncConfigSet ? '(already set — paste new value to update)' : 'Paste your GitHub token here (ghp_...)'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">Needs repo read access to download mcq_database.xlsx from your private repo</p>
                </div>
                <div className="flex items-center justify-between pt-2">
                  <button
                    onClick={() => setShowSecretValues(!showSecretValues)}
                    className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
                  >
                    {showSecretValues ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    {showSecretValues ? 'Hide values' : 'Show values'}
                  </button>
                  <div className="flex gap-2">
                    <button
                      onClick={handleTestConnection}
                      disabled={!syncConfigSet}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                        !syncConfigSet
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                      }`}
                      title={!syncConfigSet ? 'Save config first' : 'Test that the GitHub token works'}
                    >
                      Test Connection
                    </button>
                    <button
                      onClick={handleSaveConfig}
                      disabled={savingConfig || !syncSecret.trim() || !githubToken.trim()}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                        savingConfig || !syncSecret.trim() || !githubToken.trim()
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : 'bg-emerald-600 text-white hover:bg-emerald-700'
                      }`}
                    >
                      <Save className="w-4 h-4" />
                      {savingConfig ? 'Saving...' : 'Save Configuration'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <p className="text-sm text-gray-500 mb-4">
            <strong>Re-seed from File</strong> imports directly from mcq_database.xlsx on the server (no GitHub token needed).
            <strong>Sync Now</strong> downloads from the GitHub repository (requires token).
          </p>
          {syncLogs.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="pb-2 pr-4">Time</th>
                    <th className="pb-2 pr-4">Trigger</th>
                    <th className="pb-2 pr-4">Inserted</th>
                    <th className="pb-2 pr-4">Updated</th>
                    <th className="pb-2 pr-4">Unchanged</th>
                    <th className="pb-2 pr-4">Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {syncLogs.slice(0, 10).map((log, i) => (
                    <tr key={i} className={`border-b last:border-0 hover:bg-slate-50 ${log.status === 'error' ? 'bg-red-50' : ''}`}>
                      <td className="py-2 pr-4">
                        <div className="flex items-center gap-1.5">
                          {log.status === 'error' ? (
                            <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
                          ) : (
                            <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                          )}
                          <span>{new Date(log.timestamp).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                      </td>
                      <td className="py-2 pr-4">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          log.status === 'error'
                            ? 'bg-red-100 text-red-700'
                            : log.triggered_by === 'github_action'
                              ? 'bg-violet-100 text-violet-700'
                              : 'bg-cyan-100 text-cyan-700'
                        }`}>
                          {log.status === 'error' ? 'Error' : log.triggered_by === 'github_action' ? 'GitHub' : 'Manual'}
                        </span>
                      </td>
                      {log.status === 'error' ? (
                        <td colSpan={4} className="py-2 pr-4 text-red-600 text-xs">
                          {log.error?.substring(0, 200)}
                        </td>
                      ) : (
                        <>
                          <td className="py-2 pr-4 text-emerald-600 font-medium">+{log.inserted}</td>
                          <td className="py-2 pr-4 text-amber-600 font-medium">{log.updated}</td>
                          <td className="py-2 pr-4 text-gray-400">{log.unchanged}</td>
                          <td className="py-2 pr-4 text-gray-500">{log.duration_seconds}s</td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-4 text-gray-400">
              <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No syncs yet. {syncConfigSet ? 'Click "Sync Now" to import questions from GitHub.' : 'Configure settings first, then sync.'}</p>
            </div>
          )}
        </div>

        {/* ─── Question Text Fixes ─────────────────────────────────── */}
        <div id="question-fixes-section" className="bg-white rounded-xl p-6 mb-8" style={{ boxShadow: 'var(--shadow-card)' }}>
          <div className="flex items-center gap-3 mb-4">
            <Icon3D icon={Wrench} colour="turquoise" size="sm" />
            <h2 className="text-xl font-bold text-gray-800">Question Text Fixes</h2>
          </div>
          <p className="text-gray-600 mb-4">
            Scan your question bank for common text issues and fix them in bulk. No redeployment needed — changes are applied directly to the database.
          </p>
          <div className="bg-slate-50 rounded-lg p-4 mb-4 border border-slate-200">
            <p className="text-sm font-medium text-gray-700 mb-2">Fix types:</p>
            <ul className="text-sm text-gray-600 space-y-1 ml-4 list-disc">
              <li>Replace <code className="bg-gray-200 px-1 rounded">:?</code> with <code className="bg-gray-200 px-1 rounded">?</code> in question text</li>
              <li>Remove <code className="bg-gray-200 px-1 rounded">(A</code>, <code className="bg-gray-200 px-1 rounded">(B</code> etc. prefixes from answer options</li>
              <li>Remove lecture/slide/image references from questions</li>
            </ul>
          </div>

          {/* Preview Button */}
          {!fixPreview && !fixApplyResult && (
            <button
              onClick={async () => {
                setFixPreviewLoading(true);
                setFixApplyResult(null);
                try {
                  const res = await previewQuestionFixes();
                  setFixPreview(res.data);
                } catch (err) {
                  const detail = err.response?.data?.detail || err.response?.data?.message || JSON.stringify(err.response?.data) || err.message;
                  alert('Error loading preview: ' + detail);
                } finally {
                  setFixPreviewLoading(false);
                }
              }}
              disabled={fixPreviewLoading}
              className="flex items-center gap-2 px-5 py-3 text-white rounded-xl font-medium transition-all"
              style={{
                background: fixPreviewLoading ? '#9ca3af' : 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                boxShadow: fixPreviewLoading ? 'none' : 'var(--shadow-3d-btn)',
              }}
            >
              {fixPreviewLoading ? (
                <><RefreshCw className="w-4 h-4 animate-spin" /> Scanning questions...</>
              ) : (
                <><Search className="w-4 h-4" /> Preview Fixes</>
              )}
            </button>
          )}

          {/* Preview Results */}
          {fixPreview && !fixApplyResult && (
            <div className="mt-4 space-y-4">
              {/* Summary Counts */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 text-center">
                  <div className="text-2xl font-bold text-gray-700">{fixPreview.summary.total_questions_scanned.toLocaleString()}</div>
                  <div className="text-sm text-gray-500">Questions Scanned</div>
                </div>
                <div className="bg-amber-50 rounded-lg p-4 border border-amber-200 text-center">
                  <div className="text-2xl font-bold text-amber-700">{fixPreview.summary.colon_question_mark_fixes.toLocaleString()}</div>
                  <div className="text-sm text-amber-600">Colon Fixes (:? &rarr; ?)</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 text-center">
                  <div className="text-2xl font-bold text-blue-700">{fixPreview.summary.option_prefix_fixes.toLocaleString()}</div>
                  <div className="text-sm text-blue-600">Option Prefix Fixes</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 border border-purple-200 text-center">
                  <div className="text-2xl font-bold text-purple-700">{fixPreview.summary.lecture_slide_image_fixes.toLocaleString()}</div>
                  <div className="text-sm text-purple-600">Lecture/Slide Fixes</div>
                </div>
              </div>

              {/* Example Previews */}
              {fixPreview.colon_fixes_preview?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Colon Fix Examples:</h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {fixPreview.colon_fixes_preview.slice(0, 5).map((ex, idx) => (
                      <div key={idx} className="grid grid-cols-2 gap-2 text-xs">
                        <div className="bg-red-50 rounded p-2 border border-red-200">
                          <span className="text-red-400 font-medium">Before: </span>
                          <span className="text-gray-700 font-mono">{ex.before}</span>
                        </div>
                        <div className="bg-green-50 rounded p-2 border border-green-200">
                          <span className="text-green-500 font-medium">After: </span>
                          <span className="text-gray-700 font-mono">{ex.after}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {fixPreview.reference_fixes_preview?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Lecture/Slide Removal Examples:</h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {fixPreview.reference_fixes_preview.slice(0, 5).map((ex, idx) => (
                      <div key={idx} className="grid grid-cols-2 gap-2 text-xs">
                        <div className="bg-red-50 rounded p-2 border border-red-200">
                          <span className="text-red-400 font-medium">Before: </span>
                          <span className="text-gray-700 font-mono">{ex.before}</span>
                        </div>
                        <div className="bg-green-50 rounded p-2 border border-green-200">
                          <span className="text-green-500 font-medium">After: </span>
                          <span className="text-gray-700 font-mono">{ex.after}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {fixPreview.option_fixes_preview?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Option Prefix Fix Examples:</h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {fixPreview.option_fixes_preview.slice(0, 5).map((ex, idx) => (
                      <div key={idx} className="grid grid-cols-2 gap-2 text-xs">
                        <div className="bg-red-50 rounded p-2 border border-red-200">
                          <span className="text-red-400 font-medium">Before: </span>
                          <span className="text-gray-700 font-mono">{ex.before}</span>
                        </div>
                        <div className="bg-green-50 rounded p-2 border border-green-200">
                          <span className="text-green-500 font-medium">After: </span>
                          <span className="text-gray-700 font-mono">{ex.after}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No fixes needed */}
              {fixPreview.summary.colon_question_mark_fixes === 0 && fixPreview.summary.option_prefix_fixes === 0 && fixPreview.summary.lecture_slide_image_fixes === 0 && (
                <div className="bg-green-50 rounded-lg p-4 border border-green-200 text-center">
                  <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                  <p className="text-green-700 font-medium">All questions look good! No fixes needed.</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                {(fixPreview.summary.colon_question_mark_fixes > 0 || fixPreview.summary.option_prefix_fixes > 0 || fixPreview.summary.lecture_slide_image_fixes > 0) && (
                  <>
                    {!showFixConfirm ? (
                      <button
                        onClick={() => setShowFixConfirm(true)}
                        className="flex items-center gap-2 px-5 py-3 text-white rounded-xl font-medium transition-all"
                        style={{
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          boxShadow: 'var(--shadow-3d-btn)',
                        }}
                      >
                        <CheckCircle className="w-4 h-4" /> Apply All Fixes
                      </button>
                    ) : (
                      <div className="bg-amber-50 rounded-lg p-4 border border-amber-300 flex-1">
                        <p className="text-amber-800 font-medium mb-3">
                          This will modify {(fixPreview.summary.colon_question_mark_fixes + fixPreview.summary.lecture_slide_image_fixes).toLocaleString()} question texts and {fixPreview.summary.option_prefix_fixes.toLocaleString()} answer options. This cannot be undone. Are you sure?
                        </p>
                        <div className="flex gap-3">
                          <button
                            onClick={async () => {
                              setFixApplyLoading(true);
                              try {
                                const res = await applyQuestionFixes();
                                setFixApplyResult(res.data);
                                setFixPreview(null);
                                setShowFixConfirm(false);
                              } catch (err) {
                                const detail = err.response?.data?.detail || err.response?.data?.message || JSON.stringify(err.response?.data) || err.message;
                                alert('Error applying fixes: ' + detail);
                              } finally {
                                setFixApplyLoading(false);
                              }
                            }}
                            disabled={fixApplyLoading}
                            className="flex items-center gap-2 px-5 py-3 text-white rounded-xl font-medium transition-all"
                            style={{
                              background: fixApplyLoading ? '#9ca3af' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                              boxShadow: fixApplyLoading ? 'none' : 'var(--shadow-3d-btn)',
                            }}
                          >
                            {fixApplyLoading ? (
                              <><RefreshCw className="w-4 h-4 animate-spin" /> Applying fixes...</>
                            ) : (
                              <><CheckCircle className="w-4 h-4" /> Yes, Apply Fixes</>
                            )}
                          </button>
                          <button
                            onClick={() => setShowFixConfirm(false)}
                            disabled={fixApplyLoading}
                            className="px-5 py-3 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium transition-all"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
                <button
                  onClick={() => { setFixPreview(null); setShowFixConfirm(false); }}
                  className="px-5 py-3 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium transition-all"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {/* Apply Results */}
          {fixApplyResult && (
            <div className="mt-4">
              <div className="bg-green-50 rounded-lg p-5 border border-green-200">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <h3 className="text-lg font-semibold text-green-800">Fixes Applied Successfully</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-white rounded-lg p-3 border border-green-200 text-center">
                    <div className="text-xl font-bold text-green-700">{fixApplyResult.questions_text_fixed?.toLocaleString()}</div>
                    <div className="text-sm text-green-600">Questions Fixed</div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-green-200 text-center">
                    <div className="text-xl font-bold text-green-700">{fixApplyResult.options_prefix_fixed?.toLocaleString()}</div>
                    <div className="text-sm text-green-600">Options Fixed</div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-green-200 text-center">
                    <div className="text-xl font-bold text-green-700">{fixApplyResult.total_questions_scanned?.toLocaleString()}</div>
                    <div className="text-sm text-green-600">Total Scanned</div>
                  </div>
                </div>
                <button
                  onClick={() => { setFixApplyResult(null); }}
                  className="px-4 py-2 text-gray-600 bg-white hover:bg-gray-50 rounded-lg font-medium border border-gray-200 transition-all"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ─── Custom Find & Replace ──────────────────────────────── */}
        <div id="find-replace-section" className="bg-white rounded-xl p-6 mb-8" style={{ boxShadow: 'var(--shadow-card)' }}>
          <div className="flex items-center gap-3 mb-4">
            <Icon3D icon={Search} colour="turquoise" size="sm" />
            <h2 className="text-xl font-bold text-gray-800">Custom Find & Replace</h2>
          </div>
          <p className="text-gray-600 mb-4">
            Search for any text across your question database and replace it. Select which fields to search, enter what to find and what to replace it with, then preview before applying.
          </p>

          {/* Field Selection */}
          <div className="bg-slate-50 rounded-lg p-4 mb-4 border border-slate-200">
            <p className="text-sm font-medium text-gray-700 mb-3">Search in these fields:</p>
            <div className="flex flex-wrap gap-3">
              {[
                { key: 'question', label: 'Question Text' },
                { key: 'option0', label: 'Option A' },
                { key: 'option1', label: 'Option B' },
                { key: 'option2', label: 'Option C' },
                { key: 'option3', label: 'Option D' },
                { key: 'option4', label: 'Option E' },
                { key: 'explanation', label: 'Explanation' },
                { key: 'category', label: 'Category' },
                { key: 'sub_category', label: 'Sub-Category' },
              ].map(({ key, label }) => (
                <label key={key} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer bg-white px-3 py-2 rounded-lg border border-gray-200 hover:border-cyan-300 transition-colors">
                  <input
                    type="checkbox"
                    checked={frFields.includes(key)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFrFields(prev => [...prev, key]);
                      } else {
                        setFrFields(prev => prev.filter(f => f !== key));
                      }
                    }}
                    className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                  />
                  {label}
                </label>
              ))}
            </div>
            <div className="flex gap-3 mt-3">
              <button
                onClick={() => setFrFields(['question', 'option0', 'option1', 'option2', 'option3', 'option4', 'explanation', 'category', 'sub_category'])}
                className="text-xs text-cyan-600 hover:text-cyan-800 font-medium"
              >
                Select All
              </button>
              <button
                onClick={() => setFrFields([])}
                className="text-xs text-gray-500 hover:text-gray-700 font-medium"
              >
                Clear All
              </button>
            </div>
          </div>

          {/* Find & Replace Inputs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Find:</label>
              <input
                type="text"
                value={frFind}
                onChange={(e) => setFrFind(e.target.value)}
                placeholder="Text or symbol to find..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Replace with:</label>
              <input
                type="text"
                value={frReplace}
                onChange={(e) => setFrReplace(e.target.value)}
                placeholder="Replacement text (leave empty to delete)"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
              />
            </div>
          </div>

          {/* Options */}
          <div className="flex items-center gap-4 mb-4">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={frCaseSensitive}
                onChange={(e) => setFrCaseSensitive(e.target.checked)}
                className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
              />
              Case sensitive
            </label>
          </div>

          {/* Preview Button */}
          {!frPreview && !frApplyResult && (
            <button
              onClick={async () => {
                if (!frFind.trim()) { alert('Please enter text to find.'); return; }
                if (frFields.length === 0) { alert('Please select at least one field.'); return; }
                setFrPreviewLoading(true);
                setFrApplyResult(null);
                try {
                  const res = await previewFindReplace({
                    find: frFind,
                    replace: frReplace,
                    fields: frFields,
                    case_sensitive: frCaseSensitive,
                  });
                  setFrPreview(res.data);
                } catch (err) {
                  const detail = err.response?.data?.detail || err.message;
                  alert('Error loading preview: ' + detail);
                } finally {
                  setFrPreviewLoading(false);
                }
              }}
              disabled={frPreviewLoading || !frFind.trim() || frFields.length === 0}
              className="flex items-center gap-2 px-5 py-3 text-white rounded-xl font-medium transition-all disabled:opacity-50"
              style={{
                background: frPreviewLoading ? '#9ca3af' : 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                boxShadow: frPreviewLoading ? 'none' : 'var(--shadow-3d-btn)',
              }}
            >
              {frPreviewLoading ? (
                <><RefreshCw className="w-4 h-4 animate-spin" /> Scanning questions...</>
              ) : (
                <><Search className="w-4 h-4" /> Preview Changes</>
              )}
            </button>
          )}

          {/* Preview Results */}
          {frPreview && !frApplyResult && (
            <div className="mt-4 space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 text-center">
                  <div className="text-2xl font-bold text-gray-700">{frPreview.total_scanned?.toLocaleString()}</div>
                  <div className="text-sm text-gray-500">Questions Scanned</div>
                </div>
                <div className="bg-amber-50 rounded-lg p-4 border border-amber-200 text-center">
                  <div className="text-2xl font-bold text-amber-700">{frPreview.questions_affected?.toLocaleString()}</div>
                  <div className="text-sm text-amber-600">Questions Affected</div>
                </div>
                <div className="bg-cyan-50 rounded-lg p-4 border border-cyan-200 text-center">
                  <div className="text-2xl font-bold text-cyan-700">{frPreview.total_replacements?.toLocaleString()}</div>
                  <div className="text-sm text-cyan-600">Total Replacements</div>
                </div>
              </div>

              {/* Examples */}
              {frPreview.examples?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">
                    Examples (showing {Math.min(frPreview.examples.length, 10)} of {frPreview.total_replacements?.toLocaleString()} replacements):
                  </h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {frPreview.examples.slice(0, 10).map((ex, idx) => (
                      <div key={idx} className="text-xs">
                        <div className="text-gray-500 font-medium mb-1 capitalize">Field: {ex.field}</div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="bg-red-50 rounded p-2 border border-red-200">
                            <span className="text-red-400 font-medium">Before: </span>
                            <span className="text-gray-700 font-mono break-all">{ex.before}</span>
                          </div>
                          <div className="bg-green-50 rounded p-2 border border-green-200">
                            <span className="text-green-500 font-medium">After: </span>
                            <span className="text-gray-700 font-mono break-all">{ex.after}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No matches */}
              {frPreview.questions_affected === 0 && (
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 text-center">
                  <Search className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                  <p className="text-blue-700 font-medium">No matches found for "{frPreview.find}"</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                {frPreview.questions_affected > 0 && (
                  <>
                    {!showFrConfirm ? (
                      <button
                        onClick={() => setShowFrConfirm(true)}
                        className="flex items-center gap-2 px-5 py-3 text-white rounded-xl font-medium transition-all"
                        style={{
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          boxShadow: 'var(--shadow-3d-btn)',
                        }}
                      >
                        <CheckCircle className="w-4 h-4" /> Apply All Replacements
                      </button>
                    ) : (
                      <div className="bg-amber-50 rounded-lg p-4 border border-amber-300 flex-1">
                        <p className="text-amber-800 font-medium mb-1">
                          This will replace "{frPreview.find}" with "{frPreview.replace}" in {frPreview.questions_affected?.toLocaleString()} questions ({frPreview.total_replacements?.toLocaleString()} total replacements).
                        </p>
                        <p className="text-amber-700 text-sm mb-3">This cannot be undone. Are you sure?</p>
                        <div className="flex gap-3">
                          <button
                            onClick={async () => {
                              setFrApplyLoading(true);
                              try {
                                const res = await applyFindReplace({
                                  find: frFind,
                                  replace: frReplace,
                                  fields: frFields,
                                  case_sensitive: frCaseSensitive,
                                });
                                setFrApplyResult(res.data);
                                setFrPreview(null);
                                setShowFrConfirm(false);
                              } catch (err) {
                                const detail = err.response?.data?.detail || err.message;
                                alert('Error applying replacements: ' + detail);
                              } finally {
                                setFrApplyLoading(false);
                              }
                            }}
                            disabled={frApplyLoading}
                            className="flex items-center gap-2 px-5 py-3 text-white rounded-xl font-medium transition-all"
                            style={{
                              background: frApplyLoading ? '#9ca3af' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                              boxShadow: frApplyLoading ? 'none' : 'var(--shadow-3d-btn)',
                            }}
                          >
                            {frApplyLoading ? (
                              <><RefreshCw className="w-4 h-4 animate-spin" /> Applying...</>
                            ) : (
                              <><CheckCircle className="w-4 h-4" /> Yes, Apply Replacements</>
                            )}
                          </button>
                          <button
                            onClick={() => setShowFrConfirm(false)}
                            disabled={frApplyLoading}
                            className="px-5 py-3 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium transition-all"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
                <button
                  onClick={() => { setFrPreview(null); setShowFrConfirm(false); }}
                  className="px-5 py-3 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium transition-all"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {/* Apply Results */}
          {frApplyResult && (
            <div className="mt-4">
              <div className="bg-green-50 rounded-lg p-5 border border-green-200">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <h3 className="text-lg font-semibold text-green-800">Replacements Applied Successfully</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                  <div className="bg-white rounded-lg p-3 border border-green-200 text-center">
                    <div className="text-xl font-bold text-green-700">{frApplyResult.questions_updated?.toLocaleString()}</div>
                    <div className="text-sm text-green-600">Questions Updated</div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-green-200 text-center">
                    <div className="text-xl font-bold text-green-700">{frApplyResult.total_replacements?.toLocaleString()}</div>
                    <div className="text-sm text-green-600">Total Replacements</div>
                  </div>
                </div>
                <p className="text-sm text-green-700 mb-3">
                  Replaced "{frApplyResult.find}" with "{frApplyResult.replace}"
                </p>
                <button
                  onClick={() => { setFrApplyResult(null); }}
                  className="px-4 py-2 text-gray-600 bg-white hover:bg-gray-50 rounded-lg font-medium border border-gray-200 transition-all"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ─── Text Quality Fixes ───────────────────────────────────── */}
        <div className="bg-white rounded-xl p-6 mb-8" style={{ boxShadow: 'var(--shadow-card)' }}>
          <div className="flex items-center gap-3 mb-4">
            <Icon3D icon={Type} colour="turquoise" size="sm" />
            <h2 className="text-xl font-bold text-gray-800">Text Quality Fixes</h2>
          </div>
          <p className="text-gray-600 mb-4">
            Enforce proper punctuation, capitalisation and whitespace across all questions, answers and explanations.
          </p>
          <div className="bg-slate-50 rounded-lg p-4 mb-4 border border-slate-200">
            <p className="text-sm font-medium text-gray-700 mb-2">Fix types:</p>
            <ul className="text-sm text-gray-600 space-y-1 ml-4 list-disc">
              <li>Strip leading/trailing whitespace from all text</li>
              <li>Capitalise the first letter of every question, option and explanation</li>
              <li>Ensure every question ends with <code className="bg-gray-200 px-1 rounded">?</code></li>
              <li>Ensure every answer option and explanation ends with <code className="bg-gray-200 px-1 rounded">.</code></li>
            </ul>
          </div>

          {/* Preview Button */}
          {!qualityPreview && !qualityApplyResult && (
            <button
              onClick={async () => {
                setQualityPreviewLoading(true);
                setQualityApplyResult(null);
                try {
                  const res = await previewQualityFixes();
                  setQualityPreview(res.data);
                } catch (err) {
                  const detail = err.response?.data?.detail || err.response?.data?.message || JSON.stringify(err.response?.data) || err.message;
                  alert('Error loading preview: ' + detail);
                } finally {
                  setQualityPreviewLoading(false);
                }
              }}
              disabled={qualityPreviewLoading}
              className="flex items-center gap-2 px-5 py-3 text-white rounded-xl font-medium transition-all"
              style={{
                background: qualityPreviewLoading ? '#9ca3af' : 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                boxShadow: qualityPreviewLoading ? 'none' : 'var(--shadow-3d-btn)',
              }}
            >
              {qualityPreviewLoading ? (
                <><RefreshCw className="w-4 h-4 animate-spin" /> Scanning questions...</>
              ) : (
                <><Search className="w-4 h-4" /> Preview Quality Fixes</>
              )}
            </button>
          )}

          {/* Preview Results */}
          {qualityPreview && !qualityApplyResult && (
            <div className="mt-4 space-y-4">
              {/* Summary Counts */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 text-center">
                  <div className="text-2xl font-bold text-gray-700">{qualityPreview.total_scanned?.toLocaleString()}</div>
                  <div className="text-sm text-gray-500">Questions Scanned</div>
                </div>
                <div className="bg-amber-50 rounded-lg p-4 border border-amber-200 text-center">
                  <div className="text-2xl font-bold text-amber-700">{qualityPreview.questions_affected?.toLocaleString()}</div>
                  <div className="text-sm text-amber-600">Questions Affected</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 border border-purple-200 text-center">
                  <div className="text-2xl font-bold text-purple-700">{qualityPreview.fix_counts?.whitespace || 0}</div>
                  <div className="text-sm text-purple-600">Whitespace Fixes</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 text-center">
                  <div className="text-2xl font-bold text-blue-700">{qualityPreview.fix_counts?.capitalisation || 0}</div>
                  <div className="text-sm text-blue-600">Capitalisation Fixes</div>
                </div>
                <div className="bg-cyan-50 rounded-lg p-4 border border-cyan-200 text-center">
                  <div className="text-2xl font-bold text-cyan-700">{qualityPreview.fix_counts?.punctuation || 0}</div>
                  <div className="text-sm text-cyan-600">Punctuation Fixes</div>
                </div>
              </div>

              {/* Examples */}
              {qualityPreview.examples?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">
                    Examples (showing up to 20):
                  </h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {qualityPreview.examples.slice(0, 20).map((ex, idx) => (
                      <div key={idx} className="text-xs">
                        <div className="text-gray-500 font-medium mb-1 capitalize">
                          {ex.field} — {ex.changes?.join(', ')}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="bg-red-50 rounded p-2 border border-red-200">
                            <span className="text-red-400 font-medium">Before: </span>
                            <span className="text-gray-700 font-mono break-all">{ex.before}</span>
                          </div>
                          <div className="bg-green-50 rounded p-2 border border-green-200">
                            <span className="text-green-500 font-medium">After: </span>
                            <span className="text-gray-700 font-mono break-all">{ex.after}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No fixes needed */}
              {qualityPreview.questions_affected === 0 && (
                <div className="bg-green-50 rounded-lg p-4 border border-green-200 text-center">
                  <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                  <p className="text-green-700 font-medium">All questions look good! No quality fixes needed.</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                {qualityPreview.questions_affected > 0 && (
                  <>
                    {!showQualityConfirm ? (
                      <button
                        onClick={() => setShowQualityConfirm(true)}
                        className="flex items-center gap-2 px-5 py-3 text-white rounded-xl font-medium transition-all"
                        style={{
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          boxShadow: 'var(--shadow-3d-btn)',
                        }}
                      >
                        <CheckCircle className="w-4 h-4" /> Apply All Quality Fixes
                      </button>
                    ) : (
                      <div className="bg-amber-50 rounded-lg p-4 border border-amber-300 flex-1">
                        <p className="text-amber-800 font-medium mb-3">
                          This will fix punctuation, capitalisation and whitespace in {qualityPreview.questions_affected?.toLocaleString()} questions. This cannot be undone. Are you sure?
                        </p>
                        <div className="flex gap-3">
                          <button
                            onClick={async () => {
                              setQualityApplyLoading(true);
                              try {
                                const res = await applyQualityFixes();
                                setQualityApplyResult(res.data);
                                setQualityPreview(null);
                                setShowQualityConfirm(false);
                              } catch (err) {
                                const detail = err.response?.data?.detail || err.response?.data?.message || JSON.stringify(err.response?.data) || err.message;
                                alert('Error applying quality fixes: ' + detail);
                              } finally {
                                setQualityApplyLoading(false);
                              }
                            }}
                            disabled={qualityApplyLoading}
                            className="flex items-center gap-2 px-5 py-3 text-white rounded-xl font-medium transition-all"
                            style={{
                              background: qualityApplyLoading ? '#9ca3af' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                              boxShadow: qualityApplyLoading ? 'none' : 'var(--shadow-3d-btn)',
                            }}
                          >
                            {qualityApplyLoading ? (
                              <><RefreshCw className="w-4 h-4 animate-spin" /> Applying fixes...</>
                            ) : (
                              <><CheckCircle className="w-4 h-4" /> Yes, Apply Fixes</>
                            )}
                          </button>
                          <button
                            onClick={() => setShowQualityConfirm(false)}
                            disabled={qualityApplyLoading}
                            className="px-5 py-3 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium transition-all"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
                <button
                  onClick={() => { setQualityPreview(null); setShowQualityConfirm(false); }}
                  className="px-5 py-3 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium transition-all"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {/* Apply Results */}
          {qualityApplyResult && (
            <div className="mt-4">
              <div className="bg-green-50 rounded-lg p-5 border border-green-200">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <h3 className="text-lg font-semibold text-green-800">Quality Fixes Applied Successfully</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                  <div className="bg-white rounded-lg p-3 border border-green-200 text-center">
                    <div className="text-xl font-bold text-green-700">{qualityApplyResult.questions_updated?.toLocaleString()}</div>
                    <div className="text-sm text-green-600">Questions Updated</div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-green-200 text-center">
                    <div className="text-xl font-bold text-green-700">{qualityApplyResult.fix_counts?.whitespace || 0}</div>
                    <div className="text-sm text-green-600">Whitespace Fixes</div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-green-200 text-center">
                    <div className="text-xl font-bold text-green-700">{qualityApplyResult.fix_counts?.capitalisation || 0}</div>
                    <div className="text-sm text-green-600">Capitalisation Fixes</div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-green-200 text-center">
                    <div className="text-xl font-bold text-green-700">{qualityApplyResult.fix_counts?.punctuation || 0}</div>
                    <div className="text-sm text-green-600">Punctuation Fixes</div>
                  </div>
                </div>
                <button
                  onClick={() => { setQualityApplyResult(null); }}
                  className="px-4 py-2 text-gray-600 bg-white hover:bg-gray-50 rounded-lg font-medium border border-gray-200 transition-all"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ─── Encoding & Character Fixes ───────────────────────────── */}
        <div className="bg-white rounded-xl p-6 mb-8" style={{ boxShadow: 'var(--shadow-card)' }}>
          <div className="flex items-center gap-3 mb-4">
            <Icon3D icon={Zap} colour="turquoise" size="sm" />
            <h2 className="text-xl font-bold text-gray-800">Encoding & Character Fixes</h2>
          </div>
          <p className="text-gray-600 mb-4">
            Automatically fix HTML entities, corrupted apostrophes, encoding artefacts and other character issues across all questions, answers and explanations.
          </p>
          <div className="bg-slate-50 rounded-lg p-4 mb-4 border border-slate-200">
            <p className="text-sm font-medium text-gray-700 mb-2">Common fixes include:</p>
            <ul className="text-sm text-gray-600 space-y-1 ml-4 list-disc">
              <li><code className="bg-gray-200 px-1 rounded">&amp;amp;</code> → <code className="bg-gray-200 px-1 rounded">&amp;</code></li>
              <li><code className="bg-gray-200 px-1 rounded">&#39;</code> and <code className="bg-gray-200 px-1 rounded">&amp;;</code> → <code className="bg-gray-200 px-1 rounded">'</code></li>
              <li>UTF-8 mojibake characters (â€™ → ', â€œ → ", etc.)</li>
              <li>Non-breaking spaces and double spaces</li>
            </ul>
          </div>

          {/* Preview Button */}
          {!encodingPreview && !encodingApplyResult && (
            <button
              onClick={async () => {
                setEncodingPreviewLoading(true);
                setEncodingApplyResult(null);
                try {
                  const res = await previewEncodingFixes();
                  setEncodingPreview(res.data);
                } catch (err) {
                  const detail = err.response?.data?.detail || err.response?.data?.message || JSON.stringify(err.response?.data) || err.message;
                  alert('Error loading preview: ' + detail);
                } finally {
                  setEncodingPreviewLoading(false);
                }
              }}
              disabled={encodingPreviewLoading}
              className="flex items-center gap-2 px-5 py-3 text-white rounded-xl font-medium transition-all"
              style={{
                background: encodingPreviewLoading ? '#9ca3af' : 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                boxShadow: encodingPreviewLoading ? 'none' : 'var(--shadow-3d-btn)',
              }}
            >
              {encodingPreviewLoading ? (
                <><RefreshCw className="w-4 h-4 animate-spin" /> Scanning for encoding issues...</>
              ) : (
                <><Search className="w-4 h-4" /> Preview Encoding Fixes</>
              )}
            </button>
          )}

          {/* Preview Results */}
          {encodingPreview && !encodingApplyResult && (
            <div className="mt-4 space-y-4">
              {/* Summary Counts */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 text-center">
                  <div className="text-2xl font-bold text-gray-700">{encodingPreview.total_scanned?.toLocaleString()}</div>
                  <div className="text-sm text-gray-500">Questions Scanned</div>
                </div>
                <div className="bg-amber-50 rounded-lg p-4 border border-amber-200 text-center">
                  <div className="text-2xl font-bold text-amber-700">{encodingPreview.questions_affected?.toLocaleString()}</div>
                  <div className="text-sm text-amber-600">Questions Affected</div>
                </div>
                <div className="bg-cyan-50 rounded-lg p-4 border border-cyan-200 text-center">
                  <div className="text-2xl font-bold text-cyan-700">{encodingPreview.total_fixes?.toLocaleString()}</div>
                  <div className="text-sm text-cyan-600">Total Fixes</div>
                </div>
              </div>

              {/* Examples */}
              {encodingPreview.examples?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">
                    Examples (showing up to 20):
                  </h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {encodingPreview.examples.slice(0, 20).map((ex, idx) => (
                      <div key={idx} className="text-xs">
                        <div className="text-gray-500 font-medium mb-1 capitalize">
                          {ex.field} — {ex.changes?.join(', ')}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="bg-red-50 rounded p-2 border border-red-200">
                            <span className="text-red-400 font-medium">Before: </span>
                            <span className="text-gray-700 font-mono break-all">{ex.before}</span>
                          </div>
                          <div className="bg-green-50 rounded p-2 border border-green-200">
                            <span className="text-green-500 font-medium">After: </span>
                            <span className="text-gray-700 font-mono break-all">{ex.after}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No fixes needed */}
              {encodingPreview.questions_affected === 0 && (
                <div className="bg-green-50 rounded-lg p-4 border border-green-200 text-center">
                  <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                  <p className="text-green-700 font-medium">No encoding issues found! All text looks clean.</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                {encodingPreview.questions_affected > 0 && (
                  <>
                    {!showEncodingConfirm ? (
                      <button
                        onClick={() => setShowEncodingConfirm(true)}
                        className="flex items-center gap-2 px-5 py-3 text-white rounded-xl font-medium transition-all"
                        style={{
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          boxShadow: 'var(--shadow-3d-btn)',
                        }}
                      >
                        <CheckCircle className="w-4 h-4" /> Apply All Encoding Fixes
                      </button>
                    ) : (
                      <div className="bg-amber-50 rounded-lg p-4 border border-amber-300 flex-1">
                        <p className="text-amber-800 font-medium mb-3">
                          This will fix encoding issues in {encodingPreview.questions_affected?.toLocaleString()} questions ({encodingPreview.total_fixes?.toLocaleString()} total fixes). This cannot be undone. Are you sure?
                        </p>
                        <div className="flex gap-3">
                          <button
                            onClick={async () => {
                              setEncodingApplyLoading(true);
                              try {
                                const res = await applyEncodingFixes();
                                setEncodingApplyResult(res.data);
                                setEncodingPreview(null);
                                setShowEncodingConfirm(false);
                              } catch (err) {
                                const detail = err.response?.data?.detail || err.response?.data?.message || JSON.stringify(err.response?.data) || err.message;
                                alert('Error applying encoding fixes: ' + detail);
                              } finally {
                                setEncodingApplyLoading(false);
                              }
                            }}
                            disabled={encodingApplyLoading}
                            className="flex items-center gap-2 px-5 py-3 text-white rounded-xl font-medium transition-all"
                            style={{
                              background: encodingApplyLoading ? '#9ca3af' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                              boxShadow: encodingApplyLoading ? 'none' : 'var(--shadow-3d-btn)',
                            }}
                          >
                            {encodingApplyLoading ? (
                              <><RefreshCw className="w-4 h-4 animate-spin" /> Applying fixes...</>
                            ) : (
                              <><CheckCircle className="w-4 h-4" /> Yes, Apply Fixes</>
                            )}
                          </button>
                          <button
                            onClick={() => setShowEncodingConfirm(false)}
                            disabled={encodingApplyLoading}
                            className="px-5 py-3 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium transition-all"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
                <button
                  onClick={() => { setEncodingPreview(null); setShowEncodingConfirm(false); }}
                  className="px-5 py-3 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium transition-all"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {/* Apply Results */}
          {encodingApplyResult && (
            <div className="mt-4">
              <div className="bg-green-50 rounded-lg p-5 border border-green-200">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <h3 className="text-lg font-semibold text-green-800">Encoding Fixes Applied Successfully</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-white rounded-lg p-3 border border-green-200 text-center">
                    <div className="text-xl font-bold text-green-700">{encodingApplyResult.total_scanned?.toLocaleString()}</div>
                    <div className="text-sm text-green-600">Questions Scanned</div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-green-200 text-center">
                    <div className="text-xl font-bold text-green-700">{encodingApplyResult.questions_updated?.toLocaleString()}</div>
                    <div className="text-sm text-green-600">Questions Updated</div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-green-200 text-center">
                    <div className="text-xl font-bold text-green-700">{encodingApplyResult.total_fixes?.toLocaleString()}</div>
                    <div className="text-sm text-green-600">Total Fixes</div>
                  </div>
                </div>
                <button
                  onClick={() => { setEncodingApplyResult(null); }}
                  className="px-4 py-2 text-gray-600 bg-white hover:bg-gray-50 rounded-lg font-medium border border-gray-200 transition-all"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ─── Word Frequency Scanner ───────────────────────────────── */}
        <div className="bg-white rounded-xl p-6 mb-8" style={{ boxShadow: 'var(--shadow-card)' }}>
          <div className="flex items-center gap-3 mb-4">
            <Icon3D icon={BookOpen} colour="turquoise" size="sm" />
            <h2 className="text-xl font-bold text-gray-800">Word Scanner</h2>
          </div>
          <p className="text-gray-600 mb-4">
            Scan your question database for unusual or truncated words. Use <strong>frequency mode</strong> to find short words that appear often, or toggle <strong>spelling check</strong> to scan all words against an English dictionary. Click "Fix" to load a word into the Find & Replace tool above.
          </p>

          {/* Filters */}
          <div className="bg-slate-50 rounded-lg p-4 mb-4 border border-slate-200">
            <p className="text-sm font-medium text-gray-700 mb-3">Scanner settings:</p>
            <div className="flex flex-wrap gap-4 items-end">
              {/* Spelling check toggle */}
              <div>
                <label className="block text-xs text-gray-500 mb-1">Check spelling</label>
                <button
                  onClick={() => setWordSpellCheck(!wordSpellCheck)}
                  className={`relative w-11 h-6 rounded-full transition-colors ${
                    wordSpellCheck ? 'bg-cyan-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                      wordSpellCheck ? 'translate-x-5' : ''
                    }`}
                  />
                </button>
              </div>

              {/* Min/Max length inputs — hidden in spelling mode */}
              {!wordSpellCheck && (
                <>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Min word length</label>
                    <input
                      type="number"
                      value={wordMinLength}
                      onChange={(e) => setWordMinLength(Math.max(1, parseInt(e.target.value) || 1))}
                      min={1}
                      max={20}
                      className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Max word length</label>
                    <input
                      type="number"
                      value={wordMaxLength}
                      onChange={(e) => setWordMaxLength(Math.max(1, parseInt(e.target.value) || 1))}
                      min={1}
                      max={50}
                      className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
                    />
                  </div>
                </>
              )}

              <div>
                <label className="block text-xs text-gray-500 mb-1">Min frequency</label>
                <input
                  type="number"
                  value={wordMinCount}
                  onChange={(e) => setWordMinCount(Math.max(1, parseInt(e.target.value) || 1))}
                  min={1}
                  max={1000}
                  className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Max frequency</label>
                <input
                  type="number"
                  value={wordMaxCount}
                  onChange={(e) => setWordMaxCount(Math.max(1, parseInt(e.target.value) || 1))}
                  min={1}
                  max={10000}
                  className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
                />
              </div>
              <button
                onClick={async () => {
                  setWordScanLoading(true);
                  setWordScanResults(null);
                  setSpellingStep('');
                  setWordPage(0);
                  try {
                    if (wordSpellCheck) {
                      // Spelling mode: use background job to avoid timeout
                      const startRes = await startSpellingScan({ min_count: wordMinCount, max_count: wordMaxCount });
                      const jobId = startRes.data.job_id;
                      setSpellingStep('Starting spelling scan...');
                      // Poll for results
                      let done = false;
                      while (!done) {
                        await new Promise(r => setTimeout(r, 2000));
                        const pollRes = await getSpellingScanStatus(jobId);
                        const job = pollRes.data;
                        if (job.status === 'done') {
                          setWordScanResults(job);
                          done = true;
                        } else if (job.status === 'error') {
                          alert('Spelling scan failed: ' + (job.error || 'Unknown error'));
                          done = true;
                        } else {
                          setSpellingStep(job.step || 'Processing...');
                        }
                      }
                    } else {
                      // Frequency mode: direct request (fast)
                      const res = await getWordFrequency({
                        min_length: wordMinLength,
                        max_length: wordMaxLength,
                        min_count: wordMinCount,
                        max_count: wordMaxCount,
                      });
                      setWordScanResults(res.data);
                    }
                  } catch (err) {
                    const detail = err.response?.data?.detail || err.response?.data?.message || JSON.stringify(err.response?.data) || err.message;
                    alert('Error running word scan: ' + detail);
                  } finally {
                    setWordScanLoading(false);
                    setSpellingStep('');
                  }
                }}
                disabled={wordScanLoading}
                className="flex items-center gap-2 px-5 py-2.5 text-white rounded-xl font-medium transition-all"
                style={{
                  background: wordScanLoading ? '#9ca3af' : 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                  boxShadow: wordScanLoading ? 'none' : 'var(--shadow-3d-btn)',
                }}
              >
                {wordScanLoading ? (
                  <><RefreshCw className="w-4 h-4 animate-spin" /> {spellingStep || (wordSpellCheck ? 'Checking spelling...' : 'Scanning...')}</>
                ) : (
                  <><Search className="w-4 h-4" /> {wordSpellCheck ? 'Check Spelling' : 'Scan'}</>
                )}
              </button>
            </div>
          </div>

          {/* Results */}
          {wordScanResults && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-gray-600">
                  {wordScanResults.mode === 'spelling' ? (
                    <>
                      Found <span className="font-bold">{wordScanResults.words?.length}</span> potentially misspelled words
                      (checked {wordScanResults.candidates_checked?.toLocaleString()} words,
                      auto-learned <span className="font-bold">{wordScanResults.medical_vocab_learned?.toLocaleString()}</span> medical terms from your database).
                      <span className="text-amber-600 ml-1">Some medical terms may still appear — review before fixing.</span>
                    </>
                  ) : (
                    <>
                      Found <span className="font-bold">{wordScanResults.words?.length}</span> words matching your filters
                      (from <span className="font-bold">{wordScanResults.total_unique_words?.toLocaleString()}</span> unique words in the database)
                    </>
                  )}
                </p>
                <button
                  onClick={() => setWordScanResults(null)}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear
                </button>
              </div>
              {wordScanResults.words?.length > 0 ? (
                <>
                <div className="border border-gray-200 rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-bold text-gray-600 uppercase">Word</th>
                        <th className="px-4 py-2 text-right text-xs font-bold text-gray-600 uppercase">Frequency</th>
                        {wordScanResults.mode === 'spelling' && (
                          <th className="px-4 py-2 text-left text-xs font-bold text-gray-600 uppercase">Suggestion</th>
                        )}
                        <th className="px-4 py-2 text-right text-xs font-bold text-gray-600 uppercase">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {wordScanResults.words.slice(wordPage * WORDS_PER_PAGE, (wordPage + 1) * WORDS_PER_PAGE).map((item, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-2 font-mono text-gray-800">{item.word}</td>
                          <td className="px-4 py-2 text-right text-gray-600">{item.count?.toLocaleString()}</td>
                          {wordScanResults.mode === 'spelling' && (
                            <td className="px-4 py-2 text-left">
                              {item.suggestion ? (
                                <span className="font-mono text-green-700">{item.suggestion}</span>
                              ) : (
                                <span className="text-gray-400 text-xs italic">No suggestion</span>
                              )}
                              {item.suggestions?.length > 1 && (
                                <span className="text-xs text-gray-400 ml-2">
                                  (+{item.suggestions.length - 1} more)
                                </span>
                              )}
                            </td>
                          )}
                          <td className="px-4 py-2 text-right">
                            <button
                              onClick={() => {
                                setFrFind(item.word);
                                setFrReplace(item.suggestion || '');
                                setFrFields(['question', 'option0', 'option1', 'option2', 'option3', 'option4', 'explanation']);
                                // Scroll to the Find & Replace section
                                const frSection = document.querySelector('#find-replace-section');
                                if (frSection) {
                                  frSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }
                              }}
                              className="text-xs px-3 py-1 bg-cyan-50 text-cyan-700 hover:bg-cyan-100 rounded-lg font-medium border border-cyan-200 transition-colors"
                            >
                              Fix →
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Pagination */}
                {wordScanResults.words.length > WORDS_PER_PAGE && (
                  <div className="flex items-center justify-between mt-3 px-1">
                    <p className="text-xs text-gray-500">
                      Showing {wordPage * WORDS_PER_PAGE + 1}–{Math.min((wordPage + 1) * WORDS_PER_PAGE, wordScanResults.words.length)} of {wordScanResults.words.length} results
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setWordPage(p => Math.max(0, p - 1))}
                        disabled={wordPage === 0}
                        className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        ← Previous
                      </button>
                      <button
                        onClick={() => setWordPage(p => p + 1)}
                        disabled={(wordPage + 1) * WORDS_PER_PAGE >= wordScanResults.words.length}
                        className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Next →
                      </button>
                    </div>
                  </div>
                )}
                </>
              ) : (
                <div className="bg-green-50 rounded-lg p-4 border border-green-200 text-center">
                  <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                  <p className="text-green-700 font-medium">
                    {wordScanResults.mode === 'spelling'
                      ? 'No spelling mistakes found matching your criteria.'
                      : 'No unusual words found matching your criteria.'}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg p-4 mb-6" style={{ boxShadow: 'var(--shadow-card)' }}>
          <div className="flex flex-wrap gap-4">
            <input
              type="text"
              placeholder="Search by email or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 min-w-64 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
              data-testid="search-users"
            />
            <select
              value={filterSubscription}
              onChange={(e) => setFilterSubscription(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
            >
              <option value="all">All Users</option>
              <option value="free">Free Users</option>
              <option value="explorer">Explorer</option>
              <option value="essential">Essential</option>
              <option value="professional">Professional</option>
              <option value="elite">Elite</option>
              <option value="free_grant">Free Grants</option>
              <option value="active">Paid Active</option>
              <option value="admin">Admins</option>
              <option value="deactivated">Deactivated</option>
            </select>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg overflow-hidden" style={{ boxShadow: 'var(--shadow-card)' }}>
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">User</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Subscription Type</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Usage</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Verified</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Joined</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleUserClick(user)}>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center mr-3">
                        <span className="text-white font-bold">{user.email?.charAt(0).toUpperCase()}</span>
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{user.full_name || 'No name'}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                        {user.is_admin && (
                          <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full font-bold">Admin</span>
                        )}
                        {user.is_active === false && (
                          <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded-full font-bold ml-1">Deactivated</span>
                        )}
                        {user.merged_into && (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full font-bold ml-1">Merged</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {getSubscriptionBadge(user)}
                    {user.subscription_end && (
                      <div className="text-xs text-gray-500 mt-1">
                        Expires: {new Date(user.subscription_end).toLocaleDateString('en-AU')}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      <div className="font-medium">{user.total_questions_answered || 0} questions</div>
                      <div className="text-gray-500">{user.total_sessions || 0} sessions</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {user.email_verified ? (
                      <span className="text-green-600">✓ Verified</span>
                    ) : (
                      <span className="text-cyan-500">Pending</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {new Date(user.created_at).toLocaleDateString('en-AU')}
                  </td>
                  <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => handleUserClick(user)}
                      className="px-3 py-1 text-white rounded text-sm font-medium transition-all"
                      style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', boxShadow: 'var(--shadow-3d-btn)' }}
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {filteredUsers.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No users found matching your criteria
            </div>
          )}
        </div>

        {/* User Management Modal with Subscription History */}
        {showUserModal && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">{selectedUser.full_name || 'User'}</h2>
                    <p className="text-gray-500">{selectedUser.email}</p>
                    <div className="flex gap-2 mt-2">
                      {getSubscriptionBadge(selectedUser)}
                      {selectedUser.email_verified ? (
                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">Email Verified</span>
                      ) : (
                        <span className="px-2 py-1 bg-cyan-100 text-cyan-700 text-xs rounded">Email Not Verified</span>
                      )}
                      {selectedUser.marketing_consent && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">Marketing Consent</span>
                      )}
                      {selectedUser.is_active === false && (
                        <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded font-bold">Account Deactivated</span>
                      )}
                      {selectedUser.merged_into && (
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded">Merged into another account</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setShowUserModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* User Details */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Country:</span>
                    <span className="ml-2 font-medium">{selectedUser.country || 'Not set'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Institution:</span>
                    <span className="ml-2 font-medium">{selectedUser.institution || 'Not set'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Degree:</span>
                    <span className="ml-2 font-medium">{selectedUser.degree_type || 'Not set'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Year:</span>
                    <span className="ml-2 font-medium">{selectedUser.current_year || 'Not set'}</span>
                  </div>
                </div>

                {/* User Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-cyan-600">{selectedUser.total_questions_answered || 0}</div>
                    <div className="text-sm text-gray-600">Questions Answered</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-green-600">{selectedUser.qualifying_sessions || 0}/3</div>
                    <div className="text-sm text-gray-600">Qualifying Sessions</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-cyan-600">{selectedUser.ai_daily_uses || 0}/{selectedUser.ai_max_daily_uses || 0}</div>
                    <div className="text-sm text-gray-600">AI Uses Today</div>
                  </div>
                </div>

                {/* Subscription History */}
                <div>
                  <h3 className="font-bold text-gray-900 mb-3">Subscription History</h3>
                  {loadingHistory ? (
                    <div className="text-center py-4 text-gray-500">Loading history...</div>
                  ) : subscriptionHistory.length > 0 ? (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {subscriptionHistory.map((item, index) => {
                        const formatted = formatHistoryAction(item);
                        return (
                          <div key={index} className={`p-3 rounded-lg ${formatted.color}`}>
                            <div className="flex justify-between items-center">
                              <span className="font-medium">
                                {formatted.text} {formatted.extra && <span className="text-xs">{formatted.extra}</span>}
                              </span>
                              <span className="text-xs">{formatted.date}</span>
                            </div>
                            {item.end_date && (
                              <div className="text-xs mt-1 opacity-75">
                                Valid until: {new Date(item.end_date).toLocaleDateString('en-AU')}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-gray-500 text-sm bg-gray-50 rounded-lg p-3">
                      No subscription history available
                    </div>
                  )}
                </div>

                {/* Grant Free Access */}
                <div>
                  <h3 className="font-bold text-gray-900 mb-3">Grant Free Access</h3>
                  <p className="text-sm text-gray-500 mb-2">Select a tier and billing period:</p>
                  <div className="space-y-2">
                    {['explorer', 'essential', 'professional', 'elite'].map(tier => (
                      <div key={tier}>
                        <p className="text-xs font-semibold text-gray-600 mb-1 capitalize">{tier}</p>
                        <div className="grid grid-cols-3 gap-2">
                          {['monthly', 'quarterly', 'annual'].map(period => (
                            <button
                              key={`${tier}_${period}`}
                              onClick={() => handleGrantFreeAccess(selectedUser.id, `${tier}_${period}`)}
                              disabled={actionLoading}
                              className="px-3 py-1.5 bg-cyan-100 text-cyan-700 rounded hover:bg-cyan-200 text-sm font-medium capitalize"
                            >
                              {period}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Revoke Access */}
                {selectedUser.subscription_status === 'active' || selectedUser.subscription_status === 'free_grant' ? (
                  <div>
                    <h3 className="font-bold text-gray-900 mb-3">Revoke Access</h3>
                    <button
                      onClick={() => handleRevokeAccess(selectedUser.id)}
                      disabled={actionLoading}
                      className="w-full px-4 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200 font-medium"
                    >
                      Revoke Subscription
                    </button>
                  </div>
                ) : null}

                {/* Admin Toggle */}
                <div>
                  <h3 className="font-bold text-gray-900 mb-3">Admin Access</h3>
                  <button
                    onClick={() => handleToggleAdmin(selectedUser.id, !selectedUser.is_admin)}
                    disabled={actionLoading}
                    className={`w-full px-4 py-2 rounded font-medium ${
                      selectedUser.is_admin
                        ? 'bg-cyan-100 text-cyan-700 hover:bg-cyan-200'
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                  >
                    {selectedUser.is_admin ? 'Revoke Admin Access' : 'Grant Admin Access'}
                  </button>
                </div>

                {/* Merge Accounts */}
                <div>
                  <h3 className="font-bold text-gray-900 mb-3">Merge Accounts</h3>
                  <p className="text-sm text-gray-500 mb-2">Merge another user's data into this account. The other account will be deactivated.</p>
                  <button
                    onClick={() => { setShowMergeModal(true); setMergeSearchTerm(''); setMergeTargetUser(null); setMergeResult(null); }}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-white rounded-lg font-medium transition-all"
                    style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', boxShadow: 'var(--shadow-3d-btn)' }}
                  >
                    <GitMerge className="w-4 h-4" />
                    Merge With Another Account
                  </button>
                </div>

                {/* Deactivate / Reactivate Account */}
                {!selectedUser.is_admin && (
                  <div>
                    <h3 className="font-bold text-gray-900 mb-3">Account Status</h3>
                    {selectedUser.is_active === false ? (
                      <>
                        <p className="text-sm text-gray-500 mb-2">This account is deactivated. The user cannot log in but all data is preserved.</p>
                        <button
                          onClick={handleReactivate}
                          disabled={deactivateLoading}
                          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-white rounded-lg font-medium transition-all"
                          style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', boxShadow: '0 4px 10px rgba(16, 185, 129, 0.3)' }}
                        >
                          <UserCheck className="w-4 h-4" />
                          {deactivateLoading ? 'Processing...' : 'Reactivate Account'}
                        </button>
                      </>
                    ) : (
                      <>
                        {!showDeactivateConfirm ? (
                          <button
                            onClick={() => setShowDeactivateConfirm(true)}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-white rounded-lg font-medium transition-all"
                            style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', boxShadow: '0 4px 10px rgba(239, 68, 68, 0.3)' }}
                          >
                            <UserX className="w-4 h-4" />
                            Deactivate Account
                          </button>
                        ) : (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <div className="flex items-start gap-2 mb-3">
                              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="font-medium text-red-800">Are you sure you want to deactivate {selectedUser.full_name || selectedUser.email}?</p>
                                <p className="text-sm text-red-600 mt-1">They will not be able to log in. All their data will be preserved.</p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={handleDeactivate}
                                disabled={deactivateLoading}
                                className="flex-1 px-4 py-2 text-white rounded-lg font-medium transition-all"
                                style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', boxShadow: '0 4px 10px rgba(239, 68, 68, 0.3)' }}
                              >
                                {deactivateLoading ? 'Deactivating...' : 'Yes, Deactivate'}
                              </button>
                              <button
                                onClick={() => setShowDeactivateConfirm(false)}
                                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Merge Users Modal */}
        {showMergeModal && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
              {mergeResult ? (
                /* Merge Success Summary */
                <div className="p-6">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900">Merge Complete</h2>
                    <p className="text-gray-500 mt-1">All data has been merged successfully</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <h3 className="font-semibold text-gray-900 mb-2">Data Transferred</h3>
                    <div className="space-y-1 text-sm">
                      {mergeResult.collections_updated && Object.entries(mergeResult.collections_updated).map(([collection, count]) => (
                        <div key={collection} className="flex justify-between">
                          <span className="text-gray-600">{collection.replace(/_/g, ' ')}</span>
                          <span className="font-medium text-gray-900">{count} records</span>
                        </div>
                      ))}
                    </div>
                    <div className="border-t mt-2 pt-2 text-sm text-gray-500">
                      Total: {mergeResult.total_documents_updated || 0} records merged
                    </div>
                  </div>
                  <button
                    onClick={handleCloseMerge}
                    className="w-full px-4 py-2.5 text-white rounded-lg font-medium transition-all"
                    style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', boxShadow: 'var(--shadow-3d-btn)' }}
                  >
                    Done
                  </button>
                </div>
              ) : showMergeConfirm && mergeTargetUser ? (
                /* Merge Confirmation */
                <div className="p-6">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <AlertTriangle className="w-8 h-8 text-amber-600" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900">Confirm Merge</h2>
                  </div>

                  <div className="flex items-center gap-3 mb-6">
                    <div className="flex-1 bg-gray-50 rounded-lg p-3 text-center">
                      <div className="text-xs text-gray-500 mb-1">Merging from</div>
                      <div className="font-medium text-gray-900">{mergeTargetUser.full_name || 'No name'}</div>
                      <div className="text-sm text-gray-500">{mergeTargetUser.email}</div>
                      <div className="text-xs text-gray-400 mt-1">{mergeTargetUser.total_questions_answered || 0} questions</div>
                    </div>
                    <ArrowRight className="w-6 h-6 text-gray-400 flex-shrink-0" />
                    <div className="flex-1 bg-cyan-50 rounded-lg p-3 text-center border-2 border-cyan-200">
                      <div className="text-xs text-cyan-600 mb-1 font-semibold">Primary account</div>
                      <div className="font-medium text-gray-900">{selectedUser.full_name || 'No name'}</div>
                      <div className="text-sm text-gray-500">{selectedUser.email}</div>
                      <div className="text-xs text-gray-400 mt-1">{selectedUser.total_questions_answered || 0} questions</div>
                    </div>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6 text-sm">
                    <p className="text-amber-800">
                      <strong>This will:</strong> Move all of <strong>{mergeTargetUser.email}</strong>'s data (answers, sessions, progress) into <strong>{selectedUser.email}</strong>'s account. The secondary account will be deactivated.
                    </p>
                    <p className="text-amber-700 mt-1">No data will be deleted. This action creates an audit log.</p>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={handleMergeConfirm}
                      disabled={mergeLoading}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-white rounded-lg font-medium transition-all"
                      style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', boxShadow: 'var(--shadow-3d-btn)' }}
                    >
                      <GitMerge className="w-4 h-4" />
                      {mergeLoading ? 'Merging...' : 'Merge Accounts'}
                    </button>
                    <button
                      onClick={() => setShowMergeConfirm(false)}
                      disabled={mergeLoading}
                      className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200"
                    >
                      Go Back
                    </button>
                  </div>
                </div>
              ) : (
                /* User Search */
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">Merge Accounts</h2>
                      <p className="text-sm text-gray-500 mt-1">
                        Search for the account to merge <strong>into</strong> {selectedUser.full_name || selectedUser.email}'s account
                      </p>
                    </div>
                    <button onClick={handleCloseMerge} className="text-gray-400 hover:text-gray-600">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div className="relative mb-4">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search by email or name..."
                      value={mergeSearchTerm}
                      onChange={(e) => setMergeSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                      autoFocus
                    />
                  </div>

                  {mergeSearchTerm.trim() && (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {mergeFilteredUsers.length > 0 ? (
                        mergeFilteredUsers.slice(0, 10).map(u => (
                          <div
                            key={u.id}
                            onClick={() => { setMergeTargetUser(u); setShowMergeConfirm(true); }}
                            className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-cyan-300 hover:bg-cyan-50 cursor-pointer transition-all"
                          >
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center flex-shrink-0">
                              <span className="text-white font-bold">{u.email?.charAt(0).toUpperCase()}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-900 truncate">{u.full_name || 'No name'}</div>
                              <div className="text-sm text-gray-500 truncate">{u.email}</div>
                            </div>
                            <div className="text-right text-xs text-gray-400">
                              <div>{u.total_questions_answered || 0} Q's</div>
                              <div>{u.total_sessions || 0} sessions</div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-4 text-gray-500 text-sm">
                          No matching users found
                        </div>
                      )}
                    </div>
                  )}

                  {!mergeSearchTerm.trim() && (
                    <div className="text-center py-8 text-gray-400 text-sm">
                      <GitMerge className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>Type a name or email to find the account to merge</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminPage;
